import { resolvePlanningBufferPct } from '@/utils/planningBuffer';
import { CarryReasonLine } from '@/components/tickets/CarryReasonLine';
import { ContinuationRepairJobs } from '@/components/tickets/ContinuationRepairJobs';
import { Card } from '@/components/ui/card';
import { OptiFinishRow } from '@/components/tickets/OptiFinishRow';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { EditTimersModal } from './EditTimersModal';
import { TicketTimerControls } from './TicketTimerControls';
import { TicketTimerDisplay } from './TicketTimerDisplay';
import { usePermissions } from '@/hooks/usePermissions';
import { mayFinishDetailWork } from '@/lib/roles/finishAuthority';
import { Ticket } from '@/types';
import { ParkingSpotPickerDialog } from '@/components/birdseye/ParkingSpotPickerDialog';
import { CheckInVehicleDialog } from './CheckInVehicleDialog';
import { Clock, Play, Pause, CheckCircle2, AlertCircle, RotateCcw, GripVertical, AlertTriangle, FileText, Trash2, Lock, Loader2, Moon, TrendingUp, ParkingSquare } from 'lucide-react';
import { TicketDviSection } from './TicketDviSection';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
// Static (FLOYD 2026-10-26): completion must not depend on a lazily loaded chunk a deploy can remove from an open tab.
import { completeTicketWithLocation, describeCompletionRefusal, isCompletionSettled } from '@/utils/ticketCompletionWithLocation';
import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { format, parseISO } from 'date-fns';
import { toZonedTime, formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import { describeEodConflict, describeUnconfirmed } from '@/lib/scheduling/serverEodState';
import { getMinutesInShopTZ } from '@/utils/timezoneMath';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { TicketKebabMenu } from './TicketKebabMenu';
// Oct 20 addendum L1: a read-only card offers its viewer only Move Vehicle (gated on canRelocateVehicles).
import { TicketRelocateOnlyMenu } from './TicketRelocateOnlyMenu';
import { AddJobDialog } from './AddJobDialog';
import { isTicketSplit } from '@/utils/splitSuggestionRules';
import { RolloverBadge } from './RolloverBadge';
// RolloverLink removed — unified rollover system no longer shows Part X / Previous / Next
import { SplitLink } from './SplitLink';
import type { SplitPartLabelData } from '@/utils/splitGroupIdentity';
// CST-A3-D1: removed orphaned `EditCustomerModal` import. The modal had no
// JSX call site anywhere in the repo; its `isEditCustomerModalOpen` state
// declaration below was also unused (zero setter call sites). The
// component file `src/components/tickets/EditCustomerModal.tsx` was deleted
// in this round. See CST-A3-D1 audit + regression test for details.
import { EditVehicleModal } from './EditVehicleModal';
import { SpecialtyBadges } from './SpecialtyBadges';
import { ComebackResolutionModal } from './ComebackResolutionModal';
import { DiagnosticOutcomeBadge } from './DiagnosticOutcomeBadge';
// DMC-D3c-3: canonical deleted-state helper. Mirrors public.is_ticket_deleted
// (SQL) from migration 20260508120000_dmc_d2_deleted_state_helpers.sql.
import { isTicketDeleted } from '@/utils/ticketDeletedState';
import { cardPlateLabel, cardTimerRunState, cardVinLabel } from '@/lib/dayboard/cardTimerRunState';
import { useVehicleIdentity } from '@/hooks/useVehicleIdentity';
import { formatTicketNumber } from '@/lib/tickets/ticketDisplayNumber';
import { useTicketAdvisor } from '@/hooks/foh/useShopAdvisorAssignments';
import { AdvisorInitialsBadge } from '@/components/foh/AdvisorInitialsBadge';
// The EOD gate names an ACT, and not every viewer can perform it. What the
// gate says is resolved by one pure function (placement authority + canonical
// virginity) so the role boundary is provable without mounting this card.
import { resolveEodGatePresentation } from '@/utils/virginEodSchedulingConflict';
import { ReminderStatusBadge } from '../reminders/ReminderStatusBadge';
import { CustomerReplyBadge } from './CustomerReplyBadge';
import { PauseReasonBadge } from './PauseReasonBadge';
import { TicketCustodyBadge } from './TicketCustodyBadge';
import { useTicketCustody, useTicketCustodyMutation } from '@/hooks/tickets/useTicketCustody';
import { useShopTime } from '@/hooks/useShopTime';
import { formatShopTime } from '@/utils/formatShopTime';
import { getShopDayLabel, getTodayInShopTz, getTechDayHours, getTechShiftBoundaryUtc } from '@/utils/shopTime';
import { useWillingnessRollPastEod } from '@/hooks/useWillingnessRollPastEod';
import { DueOutMitigationModal } from './DueOutMitigationModal';
import { useMarketingMode } from '@/contexts/MarketingModeContext';
import { TicketDetailsPopup } from './TicketDetailsPopup';
// Oct 20 Issue 06: the card's Notes shortcut (kept on its own import line so card edits stay localized).
import { StickyNote } from 'lucide-react';

import { ManualSplitModal } from './ManualSplitModal';
import { mergeSplitTickets } from '@/utils/ticketMerging';
import { calculateWallMinutes, parseTimeToMinutes } from '@/utils/canonicalTimeMath';
// Phase A canonical derived-time record. Single source of truth for the
// time/workload values rendered on this card — replaces inline OptiFinish,
// Run Long, and AutoBump computations so they cannot diverge.
import { getDerivedTicketTime } from '@/utils/derivedTicketTime';
import { triggerBoardCascade } from '@/utils/cascadeTrigger';
// CSI-I2-A5: route the predicted_minutes revert-on-failure inside
// `handleEditJob` through the canonical schedule-mutation helper.
// `predicted_minutes` is in ALLOWED_SCHEDULE_FIELDS; the previous raw
// `.from('tickets').update(...)` write bypassed the allowlist gate.
import { mutateTicketSchedule } from '@/utils/mutateTicketSchedule';
import { 
  getJobsNeedingOutcomeForModal, 
  syncDiagnosticOutcomeFlag,
  setDiagnosticOutcomeFlagOnCompletion,
  isDiagnosticOrInspectionJob
} from '@/utils/diagnosticJobDetection';
import { useEODMonitor } from '@/hooks/useEODMonitor';
import { EditSingleJobDialog } from './EditSingleJobDialog';
// oct20e LABOR: governed job-line actions (P4 Trash state, P3 correction) and the one labor read (row 6a).
import { JobLineTrashControl, useJobLineTrashState, CompletedDiagnosticCorrectionDialog, type CorrectingJob } from './JobLineGovernedActions';
import { useTicketLaborMinutes } from '@/lib/labor/ticketLabor';
import { ServiceInfoPrompt, type ServiceInfoData } from './ServiceInfoPrompt';
import { useAuth } from '@/contexts/AuthContext';
import { notifyDayboardInvalidated } from '@/utils/dayboardInvalidation';
import {
  getTimerStartLocationState,
  requiresBayConfirmationBeforeTimerStart,
} from '@/utils/parkingToBayTimerStart';
import { TeamBadge } from '@/components/teams/TeamBadge';
import { refreshLaborForRowEdit } from '@/lib/labor/autoWriteLabor';
import { refreshTechPayForRowEdit } from '@/lib/labor/autoWriteTechPay';
import { useFeatureFlag, useExposureFlag } from '@/hooks/useFeatureFlags';
// DTC-G G2: route TicketCard's predecessor-lock anchor, live-elapsed
// counter, shop-today derivation in time-string parsing, past-time
// validation, last_edited_at write, and the per-card UI ticker through
// the canonical clock. Display-only branches at L588/590/593 are
// SAFE-A (display formatting block at L571 comment) and remain
// unmigrated. The Marketing/Film Mode override at L470 is SAFE-G
// (mock-data path).
import { clock } from '@/utils/clock';
// TIM-A1: route the per-card 60s tickers (paused-optiFinish currentTime
// refresh + predecessor-lock recheck) through the shared minute-tick
// singleton. Pre-A1 every <TicketCard> mounted on the board owned its own
// `setInterval(fn, 60_000)` for each of those two surfaces; a 20-card
// board ran 40 wall-clock-aligned 60s timer kernels for label refreshes
// alone. Post-A1 all cards share a single global 60s setInterval and
// pull the same `Date` value via React state subscription, with the
// timer kernel auto-stopping when the last card unmounts.
//
// TIM-A4: route the per-card 1s tickers (Film Mode scripted override
// elapsed display + running-session live elapsed counter) through the
// shared 1s singleton (`useSharedTimer`). Pre-A4 each card mounted in
// "Active+running" state owned its own `setInterval(updateTimer, 1000)`,
// and Film-Mode-override cards ran a second 1s kernel layered on top.
// On a 20-card board with 5 running this was 5 wall-clock-aligned 1s
// timer kernels; in Film Mode every card ran one. Post-A4 all cards
// share a single global 1s setInterval and derive `elapsedSeconds` from
// the shared `Date` value, with `useSharedTimer`'s TIM-A3 hidden-tab
// pause/catch-up behavior inherited automatically. The session-fetching
// closure stays gated by the original deps (so we do NOT refetch
// `ticket_timer_sessions` every shared tick), and pause/non-Active/
// non-running gates still freeze the display by clearing the live-tick
// snapshot to `null`.
import { useSharedMinuteTimer, useSharedTimer } from '@/hooks/useSharedTimer';

interface TicketCardProps {
  ticket: Ticket;
  technician?: {
    name: string;
    efficiencyPercentage?: number;
    // CORE-AC: the provenance of the efficiency the displayed OptiTime
    // inherits. On this same object deliberately — a badge resolved from
    // anywhere else could describe a number the reader is not looking at.
    efficiencySource?: string | null;
    efficiencySampleSize?: number | null;
    bufferPercentage?: number;
    shiftStart?: string;
    shiftEnd?: string;
  };
  isDraggable?: boolean;
  isInParkingLot?: boolean;
  parkingSpotLabel?: string;
  technicians?: Array<{ id: string; name: string; is_present: boolean }>;
  onDelete?: (ticketId: string) => void;
  onSplit?: (ticketId: string) => void;
  onMerge?: (ticketId: string) => void;
  onReassign?: () => void;
  onTicketUpdated?: () => void;
  hideTimerControls?: boolean;
  compactView?: boolean;
  hideKebabMenu?: boolean;
  isHighlighted?: boolean;
  isPendingMove?: boolean;
  isNewOptiQueue?: boolean;
  /**
   * Slice 5D-12 Batch 2: pre-computed advisor bay-hold values from
   * the board-level `useBoardAdvisorBayHold` provider. Optional —
   * when absent the card falls back to Batch 1's lower-bound
   * display ("not enough history yet"). NEVER fetched per-card.
   */
  advisorBayHoldData?: TicketAdvisorBayHoldData | null;
  /**
   * Phase 7.3: pre-computed split label data from the board-level
   * `buildSplitPartLabelDataMap`. Threaded down to `SplitLink` so the
   * badge can render real "Part X of Y" without re-running
   * reconstruction per card. Optional — when absent the SplitLink
   * falls back to the generic "Split" badge (Phase 7.2 behavior).
   */
  splitLabelData?: SplitPartLabelData | null;
}

import { PAUSE_REASONS } from '@/constants/pauseReasons';
// Slice 5D-11 Batch 1 — advisor-aware bay occupancy (display-only).
// Pure helpers; nothing here writes to scheduling, tech efficiency,
// or any DB column. Flag-gated by `advisorBayOccupancyOnDayboard`.
import {
  detectVehicleBayPresence,
  calculateAdvisorBayHoldMinutes,
  calculateBayOccupancyEstimate,
  formatAdvisorBayHoldMinutesForDisplay,
} from '@/lib/tickets/lifecycle/bayOccupancyEstimate';
import { isDependentTicket, legacyPhysicalBayId } from '@/lib/location/model';
// Slice 5D-12 Batch 2 — board-level advisor bay-hold data flows
// into this card via the new optional `advisorBayHoldData` prop.
// The hook itself is mounted at the Dashboard level (NOT per-card)
// so TicketCard stays presentational and does not query custody
// events on its own.
import type { TicketAdvisorBayHoldData } from '@/hooks/tickets/useBoardAdvisorBayHold';
import { formatEngineSizeDisplay } from '../../../supabase/functions/_shared/vehicleProfile/engineDisplay';

export const TicketCard = ({ 
  ticket, 
  technician, 
  isDraggable = false, 
  isInParkingLot = false,
  parkingSpotLabel,
  technicians = [],
  onDelete,
  onSplit,
  onMerge,
  onReassign,
  onTicketUpdated,
  hideTimerControls = false,
  compactView = false,
  hideKebabMenu = false,
  isHighlighted = false,
  isPendingMove = false,
  isNewOptiQueue = false,
  advisorBayHoldData = null,
  splitLabelData = null,
}: TicketCardProps) => {
  const [showComebackModal, setShowComebackModal] = useState(false);
  const [showDiagnosticModal, setShowDiagnosticModal] = useState(false);
  const [pendingDiagnosticJobs, setPendingDiagnosticJobs] = useState<Array<{ jobId: string; jobName: string }>>([]);
  const [pendingCompletionTicket, setPendingCompletionTicket] = useState<Ticket | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // OCT28 #6: the repair order's primary advisor (its own assignment, else the chain head's) — initials on the card.
  const ticketAdvisor = useTicketAdvisor((ticket as any).shop_id, ticket.id, (ticket as any).parent_ticket_id ?? null);
  const permissions = usePermissions();
  const { activeShop } = useAuth();
  // OCT28 RECOVERY #1: the decrypted VIN / plate / state of this card's vehicle (keyed by vehicle id; one batched read).
  const { identity: vehicleIdentity } = useVehicleIdentity(
    (ticket as any).shop_id ?? activeShop?.id,
    (ticket.vehicle as any)?.id ?? (ticket as any).vehicle_id ?? null,
    permissions.canViewVehicleIdentity,
  );
  const { formatTimeInShopTZ, toShopTZ, shopTimezone } = useShopTime();
  // FLOYD-2 — real shop culture, cached once for every card on the board.
  const willingnessRollPastEodMin = useWillingnessRollPastEod(activeShop?.id);
  const laborEngineEnabled = useFeatureFlag('labor_engine_enabled');
  const ticketFinancialsEnabled = useExposureFlag('ui_ticket_financials');
  const techPayEnabled = useExposureFlag('ui_technician_pay');
  // Lifecycle v2 — slice 3. Custody badge is flag-gated; query stays
  // disabled when off so flag-off rendering makes no network calls and
  // produces no layout shift.
  const custodyHandoffEnabled = useFeatureFlag('custodyHandoff');
  // Slice 5D-11 Batch 1 — display-only flag. Read at the top so the
  // hook order stays stable; if OFF, the block below early-returns
  // null in JSX and no helpers run.
  const advisorBayOccupancyOnDayboardEnabled = useFeatureFlag('advisorBayOccupancyOnDayboard');
  const { data: custodyRow } = useTicketCustody(
    custodyHandoffEnabled ? ticket.id : null,
  );
  // Slice 4C.1: clickable BOH-return pill. Mutation hook is mounted
  // unconditionally so hook order stays stable; it's only invoked when
  // the badge is in `pending_tech_accept` and the flag is on (the badge
  // ignores the handler in all other states).
  const custodyMutationForCard = useTicketCustodyMutation();
  const handleAcceptBackFromCard = () => {
    if (!custodyHandoffEnabled) return;
    custodyMutationForCard
      .mutateAsync({ ticketId: ticket.id, eventKind: 'boh_accept', reason: null })
      .catch(() => {
        /* mutation hook surfaces toast */
      });
  };
  const [isUpdating, setIsUpdating] = useState(false);
  const [isAddJobDialogOpen, setIsAddJobDialogOpen] = useState(false);
  const [isCheckInDialogOpen, setIsCheckInDialogOpen] = useState(false);
  const [ticketJobs, setTicketJobs] = useState(ticket.jobs || []);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  // PR #13 R-2a (W5 phantom pause): whether `elapsedSeconds` is loaded, and the instant a LIVE value was sampled at.
  // Until the session fetch resolves, elapsed is unknown — derived pause must not read the whole wall time as pause.
  const [elapsedSample, setElapsedSample] = useState<{ known: boolean; atMs: number | null }>({ known: false, atMs: null });
  const [currentTime, setCurrentTime] = useState(clock.nowMs());

  // TIM-A1: subscribe once to the shared minute-tick singleton. All per-
  // card 60s effects below derive from this single source instead of
  // owning their own setInterval. Pre-A1 each TicketCard ran two
  // independent setInterval(fn, 60000) instances for the
  // paused-currentTime refresh and the predecessor-lock recheck; on a
  // 20-card board that was 40 timer kernels firing every minute. Post-A1
  // those 40 kernels collapse to a single global setInterval inside
  // useSharedTimer.ts, with React state subscription handling the per-
  // card render notification.
  const minuteTick = useSharedMinuteTimer();
  // TIM-A4: subscribe to the shared 1s tick. The downstream live-tick
  // effect re-derives `elapsedSeconds` from this value when an active
  // session or Film-Mode override exists; pause/non-Active/non-running
  // states clear `timerLiveContext` to freeze the display.
  const sharedSecondTick = useSharedTimer();
  // TIM-A4: snapshot of the inputs needed to derive live elapsed
  // seconds from `sharedSecondTick`. Set by the session-fetching effect
  // below ONLY when the ticket should tick live (Film Mode override OR
  // Active+running with a resolved session). Cleared to `null` for
  // paused/Completed/non-Active/no-session/no-vehicle states so the
  // live-tick effect short-circuits and `elapsedSeconds` stays frozen
  // at whatever the session-fetching effect last wrote.
  const [timerLiveContext, setTimerLiveContext] = useState<
    | { mode: 'override'; baseSeconds: number; startedAt: number }
    | { mode: 'running'; baseSeconds: number; sessionStartMs: number }
    | null
  >(null);
  // CST-A3-D1: removed orphaned `isEditCustomerModalOpen` state — the
  // EditCustomerModal it gated was deleted in this round (no JSX call
  // site anywhere in the repo). Setter had zero references.
  const [isTimerLocked, setIsTimerLocked] = useState(false);
  const [lockReason, setLockReason] = useState<string>('');
  const [jobsRefreshKey, setJobsRefreshKey] = useState(0);
  const [isCollapsed, setIsCollapsed] = useState(isInParkingLot); // Auto-collapse parking lot tickets
  const [isEditVehicleModalOpen, setIsEditVehicleModalOpen] = useState(false);
  const [isDueOutModalOpen, setIsDueOutModalOpen] = useState(false);
  const [isManualSplitModalOpen, setIsManualSplitModalOpen] = useState(false);
  /**
   * 8G.2D.3.1 §9/§10 — the merge affordance, owned here.
   *
   * "Merge Split Parts" has existed in `TicketKebabMenu` since it was written,
   * gated on an `onMerge` prop that NO mount site ever supplied — so it never
   * rendered and `mergeSplitTickets` had zero callers. The fix is not a new
   * UX: `onSplit` is wired exactly this way (TicketCard owns it and opens the
   * modal; the `onSplit?` prop is vestigial indirection), so merge is owned in
   * the same place, by the same component, for the same reason.
   */
  const [showMergeConfirm, setShowMergeConfirm] = useState(false);
  const [isMerging, setIsMerging] = useState(false);
  const [bayName, setBayName] = useState<string | null>(null);
  const [movedByUserName, setMovedByUserName] = useState<string | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  // Oct 20 Issue 06: what the Command Center should open ON. null = its normal (sticky-tab) opening.
  const [detailsTarget, setDetailsTarget] = useState<{ tab: string; section?: 'notes' } | null>(null);

  // Auto-open details modal when navigated from Birdseye
  useEffect(() => {
    if (isHighlighted) {
      const params = new URLSearchParams(window.location.search);
      if (params.get('openDetails') === 'true') {
        setDetailsTarget(null);
        setIsDetailsModalOpen(true);
        params.delete('openDetails');
        const newUrl = params.toString() 
          ? `${window.location.pathname}?${params.toString()}` 
          : window.location.pathname;
        window.history.replaceState({}, '', newUrl);
      }
    }
  }, [isHighlighted]);
  const [editingJob, setEditingJob] = useState<{
    id: string;
    name: string;
    hours: number;
    notes: string;
  } | null>(null);
  // oct20e LABOR: P3 correction target, P4 per-line Trash state, and actual labor from corrected sessions (row 6a).
  const [correctingJob, setCorrectingJob] = useState<CorrectingJob | null>(null);
  const jobTrash = useJobLineTrashState(ticket.id, permissions.canEditTickets, ticketJobs);
  const { data: laborByTicket } = useTicketLaborMinutes([ticket.id], ticket.status === 'Completed');
  const recordedMinutes = laborByTicket?.get(ticket.id)?.actualLaborMinutes ?? null;
  const { isMarketingMode, timerOverrides, getTimerOverride, getTimeDisplayOverride } = useMarketingMode();
  
  
  // Local timer state override - syncs with prop but can be updated optimistically after timer actions
  const [localTimerState, setLocalTimerState] = useState<{
    timer_status: string | null;
    current_session_id: string | null;
    actually_started: boolean;
  }>({
    timer_status: (ticket as any).timer_status || null,
    current_session_id: (ticket as any).current_session_id || null,
    actually_started: !!(ticket as any).actually_started,
  });
  
  // Sync local timer state with prop changes (from realtime)
  useEffect(() => {
    setLocalTimerState({
      timer_status: (ticket as any).timer_status || null,
      current_session_id: (ticket as any).current_session_id || null,
      actually_started: !!(ticket as any).actually_started,
    });
  }, [(ticket as any).timer_status, (ticket as any).current_session_id, (ticket as any).actually_started]);
  
  // ── 8G.1: THE AUTOMATIC COMPLETION IS GONE ───────────────────────────────
  // This used to be a one-shot ref plus an effect that, the moment every job
  // read `completed || skipped` on a started ticket, CALLED the ticket
  // completion path itself. No click. CORE-AL is explicit:
  //
  //   "No automated process may mark work complete as a side effect of
  //    something else."
  //   "A person completes the work … must not complete work on anyone's
  //    behalf."
  //
  // A render effect firing a terminal act is exactly that, and its one-shot
  // guard lived in a `useRef` that did not survive remount, so a refresh
  // re-armed it.
  //
  // All jobs done is now DERIVED READINESS and nothing more: the card offers
  // the completion action, and a human decides. There is no persisted "Ready"
  // status — eligibility is computed, and the server re-checks it under lock
  // regardless of what this component believes.
  // 8G.1: the human's completion-location answer, collected BEFORE the
  // terminal call. `null` means "not asked yet" — it is never treated as
  // "general lot", because CORE-AO makes lower precision a deliberate choice
  // rather than something a missing value implies.
  // `null` means NOT ASKED YET. 8G.1 initialised this to
  // `{ kind: 'GENERAL_LOT' }` while the comment beside it said a missing
  // answer is not General Lot — so the default silently WAS the answer the
  // doctrine forbids. A preselected value is not a human assertion.
  const [pendingCompletionLocation, setPendingCompletionLocation] =
    useState<{ kind: 'PARKING_SPOT' | 'GENERAL_LOT'; spotId: string | null } | null>(null);
  const [completionLocationOpen, setCompletionLocationOpen] = useState(false);

  // A location asserted for one vehicle must never survive onto another.
  // This card is reused across tickets as the board re-renders, so the
  // assertion is re-armed whenever the identity under it changes.
  useEffect(() => {
    setPendingCompletionLocation(null);
    setCompletionLocationOpen(false);
  }, [ticket.id]);

  const allJobsDone = useMemo(() => {
    if (!ticketJobs || ticketJobs.length === 0) return false;
    return ticketJobs.every((j: any) => {
      const jobStatus = j.jobStatus || j.job_status;
      return jobStatus === 'completed' || jobStatus === 'skipped';
    });
  }, [ticketJobs]);

  const readyToComplete =
    allJobsDone &&
    !!(ticket as any).actually_started &&
    ticket.status !== 'Completed' &&
    !isTicketDeleted(ticket as any);

  // Core-100 direction 9: a Detail Specialist may finish a detail-only ticket (the server re-checks the whole visit).
  const detailSpecialistMayFinish = mayFinishDetailWork(permissions.isDetailSpecialist, ticketJobs);

  // Refetch ticket timer fields from DB and update local state
  const refreshTimerState = useCallback(async () => {
    setJobsRefreshKey(prev => prev + 1);
    const prevTimerStatus = localTimerState.timer_status;
    const { data } = await supabase
      .from('tickets')
      .select('timer_status, current_session_id, actually_started, actual_start_time, status, completed_at')
      .eq('id', ticket.id)
      .single();
    if (data) {
      setLocalTimerState({
        timer_status: (data as any).timer_status || null,
        current_session_id: (data as any).current_session_id || null,
        actually_started: !!(data as any).actually_started,
      });
    }
  }, [ticket.id]);
  
  // BUG-3.6 fix: Local state for vehicle present to avoid prop mutation
  const [localVehiclePresent, setLocalVehiclePresent] = useState(ticket.vehiclePresent);
  
  // Sync with prop changes from realtime updates
  useEffect(() => {
    setLocalVehiclePresent(ticket.vehiclePresent);
  }, [ticket.vehiclePresent]);

  // Fetch the user name who moved ticket to parking lot
  useEffect(() => {
    const fetchMovedByUser = async () => {
      const lastEditedByUserId = (ticket as any).last_edited_by_user_id;
      if (!isInParkingLot || !lastEditedByUserId) {
        setMovedByUserName(null);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('display_name, email')
          .eq('id', lastEditedByUserId)
          .maybeSingle();

        if (error) {
          console.error('Error fetching moved-by user:', error);
          setMovedByUserName(null);
          return;
        }
        setMovedByUserName(data?.display_name || data?.email || 'User');
      } catch (error) {
        console.error('Error fetching moved-by user:', error);
        setMovedByUserName(null);
      }
    };

    fetchMovedByUser();
  }, [(ticket as any).last_edited_by_user_id, isInParkingLot]);

  const fetchTicketJobsWithCompletionFallback = useCallback(async () => {
    const { data } = await supabase
      .from('ticket_jobs')
      .select(`
        job_id,
        minutes_override,
        notes,
        separate_time,
        diagnostic_completed_at,
        actual_minutes,
        job_status,
        jobs!ticket_jobs_job_id_fkey (
          id,
          name,
          category,
          submenu,
          standard_minutes,
          bay_requirement,
          skill_level
        )
      `)
      .eq('ticket_id', ticket.id);

    if (!data) return;

    const rolloverSourceTicketId =
      (ticket as any).original_ticket_id ||
      (ticket as any).rollover_of_ticket_id ||
      null;

    const completionFallbackByJobId = new Map<
      string,
      {
        separate_time: boolean | null;
        diagnostic_completed_at: string | null;
        job_status: string | null;
      }
    >();

    if (rolloverSourceTicketId && rolloverSourceTicketId !== ticket.id) {
      const diagnosticJobIds = data
        .filter((tj: any) =>
          isDiagnosticOrInspectionJob({
            category: tj.jobs?.category,
            submenu: tj.jobs?.submenu,
            name: tj.jobs?.name,
          })
        )
        .map((tj: any) => tj.job_id);

      if (diagnosticJobIds.length > 0) {
        const { data: sourceJobs } = await supabase
          .from('ticket_jobs')
          .select('job_id, separate_time, diagnostic_completed_at, job_status')
          .eq('ticket_id', rolloverSourceTicketId)
          .in('job_id', diagnosticJobIds);

        (sourceJobs || []).forEach((sourceJob: any) => {
          const isCompleted =
            !!sourceJob.separate_time ||
            !!sourceJob.diagnostic_completed_at ||
            sourceJob.job_status === 'completed' ||
            sourceJob.job_status === 'skipped';

          if (isCompleted) {
            completionFallbackByJobId.set(sourceJob.job_id, sourceJob);
          }
        });
      }
    }

    const formattedJobs = data.map((tj: any) => {
      const job = tj.jobs;
      const completionFallback = completionFallbackByJobId.get(tj.job_id);

      return {
        id: job?.id || tj.job_id,
        name: job?.name || 'Unknown Job',
        category: job?.category || '',
        submenu: job?.submenu || '',
        standardMinutes: tj.minutes_override || job?.standard_minutes || 0,
        bayRequirement: job?.bay_requirement || 'general',
        skillLevel: job?.skill_level || 1,
        notes: tj.notes,
        separateTime: tj.separate_time || completionFallback?.separate_time || false,
        diagnosticCompletedAt: tj.diagnostic_completed_at || completionFallback?.diagnostic_completed_at || null,
        jobStatus: tj.job_status || completionFallback?.job_status,
      };
    });

    setTicketJobs(formattedJobs);
  }, [ticket.id, (ticket as any).original_ticket_id, (ticket as any).rollover_of_ticket_id]);

  // Re-fetch from DB so ticket cards keep full job metadata and stale rollover jobs inherit completed diagnostic/inspection state
  useEffect(() => {
    fetchTicketJobsWithCompletionFallback();
  }, [ticket.jobs, fetchTicketJobsWithCompletionFallback]);

  // Fetch bay name when bay_id changes
  useEffect(() => {
    const fetchBayName = async () => {
      const bayId = (ticket as any).bay_id;
      if (!bayId) {
        setBayName(null);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('shop_bays')
          .select('name')
          .eq('id', bayId)
          .maybeSingle();

        if (error) {
          console.error('Error fetching bay name:', error);
          setBayName(null);
          return;
        }
        setBayName(data?.name || null);
      } catch (error) {
        console.error('Error fetching bay name:', error);
        setBayName(null);
      }
    };

    fetchBayName();
  }, [(ticket as any).bay_id]);

  // Check if timer should be locked (rollover predecessor not complete).
  // TIM-A1: drop the per-card `setInterval(fn, 60_000)` and instead let
  // `minuteTick` (from `useSharedMinuteTimer`) drive the recheck cadence
  // by participating in this effect's dep array. Behavior preserved:
  // - The check still fires on mount (initial render runs the effect).
  // - It still re-fires every ~60s (whenever the global minute timer
  //   advances `minuteTick`, this effect's deps array sees a new
  //   reference and React re-runs the body).
  // - It still re-fires on ticket.id / parent_ticket_id / sequence_index
  //   / toShopTZ changes (those deps are preserved verbatim).
  // The setInterval-based version was the per-card timer kernel
  // identified in the TIM audit; collapsing all <TicketCard> instances
  // onto the single shared minute timer eliminates the per-card kernel
  // without touching the predecessor-lock decision body.
  useEffect(() => {
    const checkPredecessorLock = async () => {
      const parentTicketId = (ticket as any).parent_ticket_id;
      const sequenceIndex = (ticket as any).sequence_index;
      const rolloverOfTicketId = (ticket as any).rollover_of_ticket_id;

      // 8G.2D.6A: a chain ROOT is the ticket that names no predecessor. This
      // used to short-circuit on `sequence_index === 1`, which is a second
      // copy of the root test living outside the authority that owns it — and
      // it would have skipped past the helper's fail-closed handling of a
      // continuation whose predecessor cannot be read.
      if (!rolloverOfTicketId && (!parentTicketId || !sequenceIndex || sequenceIndex === 1)) {
        setIsTimerLocked(false);
        return;
      }

      const { isPredecessorComplete } = await import('@/utils/rolloverChain');
      const currentShopDate = toShopTZ(clock.now());
      const result = await isPredecessorComplete(
        {
          parent_ticket_id: parentTicketId,
          sequence_index: sequenceIndex,
          rollover_of_ticket_id: rolloverOfTicketId,
        },
        currentShopDate
      );

      setIsTimerLocked(!result.isComplete);
      setLockReason(result.reason || '');
    };

    checkPredecessorLock();
  }, [ticket.id, (ticket as any).parent_ticket_id, (ticket as any).sequence_index,
      (ticket as any).rollover_of_ticket_id, toShopTZ, minuteTick]);

  // Timer - only runs while Active; Paused freezes display at the time of pause.
  //
  // TIM-A4: pre-A4 this effect ran a per-card `setInterval(updateTimer,
  // 1000)` for the running-session path AND a separate
  // `setInterval(setElapsedSeconds(calcElapsed()), 1000)` for the
  // Film Mode override path. Both kernels are gone post-A4: the
  // session-fetching closure (DB lookup with retries, base-seconds
  // accumulation, status/vehicle/Active gating) still runs here on its
  // original deps, but instead of owning its own ticker it stamps
  // `timerLiveContext` and lets the downstream `sharedSecondTick`
  // effect re-derive `elapsedSeconds` once per global 1s tick. Frozen
  // states (paused/Completed/non-Active/no-session/no-vehicle/
  // accumulated-only) clear `timerLiveContext` to `null` so the live-
  // tick effect early-returns and `elapsedSeconds` stays at the value
  // this effect last wrote.
  useEffect(() => {
    const actuallyStarted = localTimerState.actually_started;
    const timerStatus = localTimerState.timer_status;
    const currentSessionId = localTimerState.current_session_id;

    let mounted = true;
    // R-2a: a (re)load of the timer state makes the elapsed value unknown until it resolves below.
    setElapsedSample({ known: false, atMs: null });

    const run = async () => {
      // Check for Film Mode timer override first
      const customerName = (ticket as any).customer_name || ticket.customerName;
      const timerOverride = isMarketingMode ? getTimerOverride(customerName) : null;

      if (timerOverride) {
        // Use scripted timer from Film Mode. TIM-A4: stamp the override
        // context so the shared-1s effect drives the live increment;
        // also seed `elapsedSeconds` here so the FIRST render after
        // mount/override-change is not a frame behind.
        const sampledAt = clock.nowMs();
        const elapsed = timerOverride.baseSeconds + Math.floor((sampledAt - timerOverride.startedAt) / 1000);
        setElapsedSeconds(elapsed);
        setElapsedSample({ known: true, atMs: sampledAt });
        setTimerLiveContext({
          mode: 'override',
          baseSeconds: timerOverride.baseSeconds,
          startedAt: timerOverride.startedAt,
        });
        return;
      }

      // Normal timer logic
      if (!ticket.vehiclePresent || !actuallyStarted) {
        setElapsedSeconds(0);
        setElapsedSample({ known: true, atMs: null });
        setTimerLiveContext(null);
        return;
      }

      // Fetch accumulated time from completed sessions
      const { getTicketTotalActualMinutes } = await import('@/utils/ticketTimerManager');
      const baseMin = await getTicketTotalActualMinutes(ticket.id);
      const baseSeconds = Math.max(0, Math.floor((baseMin || 0) * 60));

      if (!mounted) return;

      // If Paused, freeze at accumulated time
      if (timerStatus === 'paused') {
        setElapsedSeconds(baseSeconds);
        setElapsedSample({ known: true, atMs: null });
        setTimerLiveContext(null);
        return;
      }

      // If not Active (Completed, Deleted, etc.), use accumulated time
      if (ticket.status !== 'Active') {
        setElapsedSeconds(baseSeconds);
        setElapsedSample({ known: true, atMs: null });
        setTimerLiveContext(null);
        return;
      }

      // Active and running: need current session start
      if (timerStatus !== 'running' || !currentSessionId) {
        setElapsedSeconds(baseSeconds);
        setElapsedSample({ known: true, atMs: null });
        setTimerLiveContext(null);
        return;
      }

      // Retry logic for fetching session (handles race condition on resume)
      let session = null;
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          const { data } = await supabase
            .from('ticket_timer_sessions')
            .select('started_at')
            .eq('id', currentSessionId)
            .single();

          if (data) {
            session = data;
            break;
          }

          // Wait before retrying
          if (attempt < 2) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        } catch (error) {
          console.error(`Error fetching timer session (attempt ${attempt + 1}):`, error);
        }
      }

      if (!mounted) return;
      if (!session) {
        console.warn('Timer session not found after retries, showing accumulated time only');
        setElapsedSeconds(baseSeconds);
        // R-2a: a running timer whose live session is unknown has an unknown elapsed — no pause is derived from it.
        setElapsedSample({ known: false, atMs: null });
        setTimerLiveContext(null);
        return;
      }

      const sessionStartTime = new Date(session.started_at).getTime();
      // TIM-A4: seed the first paint synchronously, then hand off live
      // incrementing to the shared-1s effect via `timerLiveContext`.
      const sampledAt = clock.nowMs();
      const initialDiff = Math.max(0, Math.floor((sampledAt - sessionStartTime) / 1000));
      setElapsedSeconds(baseSeconds + initialDiff);
      setElapsedSample({ known: true, atMs: sampledAt });
      setTimerLiveContext({
        mode: 'running',
        baseSeconds,
        sessionStartMs: sessionStartTime,
      });
    };

    run();
    return () => {
      mounted = false;
    };
  }, [ticket.id, ticket.vehiclePresent, ticket.status, localTimerState.actually_started, localTimerState.timer_status, localTimerState.current_session_id, isMarketingMode, timerOverrides, getTimerOverride, (ticket as any).customer_name, ticket.customerName]);

  // TIM-A4: live-tick effect — re-derives `elapsedSeconds` from the
  // shared 1s tick whenever a Film Mode override or a running session
  // is active. When `timerLiveContext` is `null` (paused/Completed/
  // non-Active/no-session/no-vehicle), this early-returns so paused or
  // non-running tickets do NOT continue counting. Re-rendering happens
  // because `useSharedTimer` is a state subscription — the shared
  // singleton fires once per second and notifies every subscriber via
  // `setState`. Pre-A4 each TicketCard had its own setInterval feeding
  // setElapsedSeconds; post-A4 the global singleton in useSharedTimer.ts
  // is the one and only 1s kernel for live elapsed display across the
  // board. Hidden-tab pause/catch-up (TIM-A3) is inherited automatically.
  useEffect(() => {
    if (!timerLiveContext) return;
    const nowMs = sharedSecondTick.getTime();
    if (timerLiveContext.mode === 'override') {
      setElapsedSeconds(
        timerLiveContext.baseSeconds +
          Math.floor((nowMs - timerLiveContext.startedAt) / 1000),
      );
      setElapsedSample({ known: true, atMs: nowMs });
    } else {
      const diff = Math.max(
        0,
        Math.floor((nowMs - timerLiveContext.sessionStartMs) / 1000),
      );
      setElapsedSeconds(timerLiveContext.baseSeconds + diff);
      setElapsedSample({ known: true, atMs: nowMs });
    }
  }, [sharedSecondTick, timerLiveContext]);

  // Per-ticket timer session subscription removed — board-level refresh handles this


  // Update current time every minute for paused ticket optiFinish calculations.
  // TIM-A1: drop the per-card `setInterval(fn, 60_000)` and instead let
  // `minuteTick` drive the `setCurrentTime(clock.nowMs())` call. The
  // resulting `currentTime` state value still updates ~once per minute
  // and remains numerically equivalent (`clock.nowMs()` matches the
  // shared timer's underlying `clock.now()`), so the downstream useMemo
  // / useEffect deps that read `currentTime` (paused-optiFinish projected
  // finish at L909, work-end derivation at L750, wall-clock-min display
  // math at L2177/L2200) are unaffected. Mount-time initial value is
  // still set by the `useState(clock.nowMs())` initializer above; the
  // effect just keeps it advancing on the shared cadence.
  useEffect(() => {
    setCurrentTime(clock.nowMs());
  }, [minuteTick]);


  // Display-only adjustment for tickets with past start times
  // This is ONLY for UI purposes - does NOT write to database
  // The smart-assign function will handle actual scheduling based on stored start_time
  const displayStartTime = useMemo(() => {
    const actuallyStarted = (ticket as any).actually_started;
    
    // Only adjust display for tickets that haven't started and aren't paused/completed.
    // DMC-D3c-3: canonical deleted-state check (both reps).
    if (actuallyStarted || ticket.status === 'Paused' || ticket.status === 'Completed' || isTicketDeleted(ticket as any)) {
      return null; // Use actual stored time
    }
    
    try {
      let startDate: Date;
      
      // Parse start time reliably from DB timestamp
      try {
        const raw = (ticket as any).start_time as string | undefined;
        startDate = raw ? new Date(raw) : new Date();
      } catch {
        startDate = new Date();
      }
      
      const now = new Date();
      
      // If start time is in the past, calculate display time (respecting breaks)
      if (startDate < now) {
        const bumpedTime = new Date(now);
        bumpedTime.setSeconds(0, 0);
        
        // Check if bumped time falls within a break
        const tech = (ticket as any).technician;
        if (tech) {
           // R7-L-2: Use shop TZ for date label, not UTC split
           const dateStr = formatInTimeZone(bumpedTime, shopTimezone, 'yyyy-MM-dd');
          const recurringBreaks = tech.recurring_breaks || [];
          const nonRecurringBreaks = (tech.non_recurring_breaks || []).filter((br: any) => br.date === dateStr);
          const allBreaks = [...recurringBreaks, ...nonRecurringBreaks];
          
          // Find if current time is within a break
          for (const br of allBreaks) {
            if (br.start && br.end) {
              const [bStartH, bStartM] = br.start.split(':').map(Number);
              const [bEndH, bEndM] = br.end.split(':').map(Number);
              const breakStart = fromZonedTime(`${dateStr}T${br.start}:00`, shopTimezone);
              const breakEnd = fromZonedTime(`${dateStr}T${br.end}:00`, shopTimezone);
              
              // If bumped time is within break, set it to break end time
              if (bumpedTime >= breakStart && bumpedTime < breakEnd) {
                bumpedTime.setTime(breakEnd.getTime());
                break;
              }
            }
          }
        }
        
        return bumpedTime;
      }
    } catch (error) {
      console.error('Error calculating display start time:', error);
    }
    
    return null; // Use actual stored time
  }, [ticket.id, ticket.startTime, ticket.status, (ticket as any).actually_started, (ticket as any).technician, currentTime]);

  const formatElapsedTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: ticket.id, disabled: !isDraggable });

  const statusConfig: Record<string, { color: string; icon: any; label: string }> = {
    Active: { color: 'hsl(var(--status-active))', icon: Play, label: 'Active' },
    Paused: { color: 'hsl(var(--status-paused))', icon: Pause, label: 'Paused' },
    Completed: { color: 'hsl(var(--status-completed))', icon: CheckCircle2, label: 'Completed' },
    Blocked: { color: 'hsl(var(--status-blocked))', icon: AlertCircle, label: 'Blocked' },
    Deleted: { color: 'hsl(var(--destructive))', icon: AlertCircle, label: 'Deleted' }
  };

  const config = statusConfig[ticket.status] || statusConfig.Paused;
  const StatusIcon = config?.icon || AlertCircle;

  // Codex: predicted_minutes IS the canonical duration source.
  // Use it directly — do NOT re-sum from jobs as the authoritative path.
  // Jobs sum is only a fallback if predicted_minutes hasn't been set yet.
  const totalMinutes = useMemo(() => {
    const pm = (ticket as any).predicted_minutes ?? (ticket as any).predictedMinutes;
    if (pm != null && pm > 0) return pm;
    // Fallback: sum from jobs if predicted_minutes not yet populated
    const jobsTotal = ticketJobs.reduce((sum, job) => {
      if ((job as any).separateTime || (job as any).diagnosticCompletedAt) return sum;
      return sum + (job.standardMinutes || 0);
    }, 0);
    return jobsTotal > 0 ? jobsTotal : 0;
  }, [ticketJobs, (ticket as any).predicted_minutes, (ticket as any).predictedMinutes]);

  // Codex: Calculate OptiTime using canonical wall-time formula
  // predicted_minutes is the canonical source of truth for scheduling
  const calculateOptiTime = useMemo(() => {
    return calculateWallMinutes(
      totalMinutes,
      technician?.efficiencyPercentage ?? 100,
      resolvePlanningBufferPct(technician?.bufferPercentage)
    );
  }, [totalMinutes, technician?.efficiencyPercentage, technician?.bufferPercentage]);


  // Phase F — `calculateBreakMinutes` was retired: it used
  // `toZonedTime(unsuffixedISO, tz)` to construct break window instants,
  // which SHIFTS the value instead of interpreting it as shop-local time.
  // For any non-UTC shop timezone the resulting break-window UTC instants
  // were off by the shop's UTC offset (e.g. 5-6 hours for US shops), so
  // the overlap test below silently miscounted break minutes.
  //
  // The canonical `derivedTime.breakMinutes` (from `getDerivedTicketTime`)
  // constructs break windows via `fromZonedTime(unsuffixedISO, tz)` which
  // correctly returns the UTC equivalent of the shop-local wall clock. All
  // three former consumers (Break display row, dueOutViolation memo,
  // useEODMonitor prop) now consume `derivedTime.breakMinutes` directly.

  // Phase A canonical derived-time record. Composes the existing pure helpers
  // (canonicalTimeMath, nonBayHoldRolloverGuard, ticketProductionState) into
  // ONE source of truth for: techOptiTime, runLong, bay-blocking pause vs
  // non-bay hold, break + paused-during-break overlap, OptiFinish, EOD risk.
  // This memo replaces the inline OptiFinish/RunLong/AutoBump computations
  // below.
  const derivedTime = useMemo(() => {
    // Parse the canonical start instant (matches the previous OptiFinish parse path).
    let startInstant: Date | null = null;
    try {
      const rawDb = (ticket as any).start_time as string | undefined;
      if (rawDb) {
        startInstant = new Date(rawDb);
      } else if (ticket.startTime && (ticket.startTime.includes('T') || ticket.startTime.includes('Z'))) {
        startInstant = new Date(ticket.startTime);
      } else {
        // Fallback: parse time-only value like "06:00 AM" or "15:30" anchored
        // to the shop's today. R25-LOW-2: use shop timezone instead of
        // browser-local setHours. (This path is pinned by the DTC-G source
        // scan — keep getTodayInShopTz(shopTimezone) call in this file.)
        const match = ticket.startTime?.match(/(\d{1,2}):(\d{2})(?:\s*(AM|PM))?/i);
        if (match) {
          const [, hh, mm, ap] = match as unknown as [string, string, string, string?];
          let h = parseInt(hh, 10);
          const m = parseInt(mm, 10);
          if (ap) {
            const apu = ap.toUpperCase();
            if (apu === 'PM' && h < 12) h += 12;
            if (apu === 'AM' && h === 12) h = 0;
          }
          const shopDateStr = getTodayInShopTz(shopTimezone);
          startInstant = fromZonedTime(
            `${shopDateStr}T${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:00`,
            shopTimezone,
          );
        }
      }
      if (startInstant && Number.isNaN(startInstant.getTime())) startInstant = null;
    } catch {
      startInstant = null;
    }
    // For unstarted, past-time-bumped tickets the existing logic uses
    // displayStartTime — preserve that behaviour so the canonical record
    // matches the card-level start render.
    const actuallyStarted = (ticket as any).actually_started === true;
    if (displayStartTime && !actuallyStarted && ticket.status !== 'Paused') {
      startInstant = displayStartTime;
    }

    // Pre-resolve break windows into UTC instants. The helper stays
    // timezone-agnostic; the caller does the conversion. Matches the existing
    // recurring/non-recurring break scan at the existing calculateBreakMinutes.
    const tech = (ticket as any).technician;
    const breakWindowsUtc: Array<{ startUtcMs: number; endUtcMs: number }> = [];
    if (startInstant && tech) {
      try {
        const dateStr = formatInTimeZone(startInstant, shopTimezone, 'yyyy-MM-dd');
        const recurring = tech.recurring_breaks || [];
        const nonRecurring = (tech.non_recurring_breaks || []).filter(
          (br: any) => br && br.date === dateStr,
        );
        for (const br of [...recurring, ...nonRecurring]) {
          if (!br || !br.start || !br.end) continue;
          const bs = fromZonedTime(`${dateStr}T${br.start}:00`, shopTimezone);
          const be = fromZonedTime(`${dateStr}T${br.end}:00`, shopTimezone);
          if (Number.isFinite(bs.getTime()) && Number.isFinite(be.getTime())) {
            breakWindowsUtc.push({ startUtcMs: bs.getTime(), endUtcMs: be.getTime() });
          }
        }
      } catch (error) {
        console.error('[derivedTime] break-window resolution failed', error);
      }
    }

    return getDerivedTicketTime({
      ticket: ticket as any,
      technician: {
        efficiencyPercentage: technician?.efficiencyPercentage ?? 100,
        bufferPercentage: resolvePlanningBufferPct(technician?.bufferPercentage),
        recurring_breaks: tech?.recurring_breaks ?? null,
        non_recurring_breaks: tech?.non_recurring_breaks ?? null,
        shift_start: tech?.shift_start ?? null,
        shift_end: tech?.shift_end ?? null,
      },
      jobs: ticketJobs as any,
      shopConfig: {
        // Phase D: helper-level EOD detection is display-only. The actual
        // rollover dispatch is still owned by useEODMonitor below.
        //
        // FLOYD-2: this was hard-coded to 0, so the card refused work the
        // shop had said it would stay late for. Display-only does not mean
        // free to disagree with the authority it is displaying.
        willingnessRollPastEodMin: willingnessRollPastEodMin,
        breakFailsafeAutoPauseEnabled: false,
        eodRiskBandMin: 30,
      },
      // FLOYD-2 — the technician's close for THIS ticket's shop-local day, as
      // an instant. Built exactly as `useAutoBump` builds it, so the card
      // shows the rollover authority's boundary rather than a second opinion
      // derived from the flat weekly MODE.
      shiftEndUtc: (() => {
        if (!startInstant) return null;
        try {
          const shopDayLabel = formatInTimeZone(startInstant, shopTimezone, 'yyyy-MM-dd');
          const close = getTechDayHours(tech as never, shopDayLabel)?.close ?? tech?.shift_end;
          if (!close) return null;
          return getTechShiftBoundaryUtc(shopDayLabel, close, shopTimezone);
        } catch (error) {
          // An unresolvable close means the gate does not fire. Silence beats
          // a confident refusal built on a boundary we could not work out.
          console.error('[derivedTime] shift-end boundary resolution failed', error);
          return null;
        }
      })(),
      startInstant,
      nowInstant: new Date(currentTime),
      liveElapsedSeconds: elapsedSeconds,
      liveElapsedKnown: elapsedSample.known,
      liveElapsedSampledAtMs: elapsedSample.atMs,
      breakWindowsUtc,
    });
  }, [
    ticket,
    ticketJobs,
    technician?.efficiencyPercentage,
    technician?.bufferPercentage,
    displayStartTime,
    shopTimezone,
    currentTime,
    elapsedSeconds,
    elapsedSample,
  ]);

  // EOD Monitor for paused tickets. OCT28 WP-2b-2b-ii-b2-A (Option B): presents the database's end-of-day judgement
  // (ticket_eod_status) — the existing EOD modal on a confirmed conflict, an explicit line otherwise. It no longer
  // estimates a finish from elapsed time.
  const serverEod = useEODMonitor({
    ticketId: ticket.id,
    ticketStatus: ticket.status,
    actuallyStarted: localTimerState.actually_started,
    timerStatus: localTimerState.timer_status || 'stopped',
    optiFinish: (ticket as any).opti_finish ?? null,
    techId: (ticket as any).assigned_tech_id,
    customerName: (ticket as any).customer_name || ticket.customerName || 'Unknown Customer',
    shopTier: activeShop?.tier || 'CORE',
    shopId: (ticket as any).shop_id || activeShop?.id,
  });
  const dueOutViolation = useMemo(() => {
    // Only check for non-completed tickets with a due_out time.
    if (ticket.status === 'Completed' || !ticket.dueOut) {
      return null;
    }
    // Phase F — consume the canonical derived finish instead of recomputing
    // Start + OptiTime + Break inline. The canonical finish already factors
    // in bay-blocking pause, run-long, and break overlap (Bucket 1 §2.10),
    // so an active ticket that's already past its predicted finish now
    // correctly triggers the Due Out modal. Pre-Phase-F this memo only
    // added break time, under-reporting projected finish for run-long
    // tickets.
    const predictedFinish = derivedTime.optiFinishUtc;
    if (!predictedFinish) return null;

    try {
      const dueOutTime = parseISO((ticket as any).due_out || ticket.dueOut);
      if (predictedFinish > dueOutTime) {
        return {
          dueOutTime,
          predictedFinish,
          delayMinutes: Math.round((predictedFinish.getTime() - dueOutTime.getTime()) / 60000),
        };
      }
      return null;
    } catch (error) {
      console.error('[DueOutDetection] Error calculating violation:', error);
      return null;
    }
  }, [ticket.status, ticket.dueOut, (ticket as any).due_out, derivedTime.optiFinishUtc]);

  // Auto-show due out modal when violation detected (only once per ticket)
  useEffect(() => {
    if (dueOutViolation && !isDueOutModalOpen) {
      console.log(`[DueOutDetection] Violation detected for ticket ${ticket.id}: ${dueOutViolation.delayMinutes} min delay`);
      setIsDueOutModalOpen(true);
    }
  }, [dueOutViolation?.delayMinutes, ticket.id]);


  // Phase B — OptiFinish is now derived from the canonical record so the
  // formula respects bay-blocking vs non-bay hold doctrine. Old inline
  // computation (Pre-Phase-B) summed ALL pause minutes into OptiFinish
  // regardless of physical location, which let held-in-parking tickets
  // silently extend past EOD (Bucket 4.5 §4.5.8 violation).
  // What the EOD gate should say to THIS viewer about THIS ticket. Decided by
  // `resolveEodGatePresentation` (placement authority + canonical virginity) so
  // the three branches below are a switch over a tested value rather than three
  // conditions that can drift apart.
  const eodGatePresentation = useMemo(
    () => resolveEodGatePresentation({
      requiresRolloverGate: derivedTime.requiresRolloverGate,
      canAssignTickets: permissions.canAssignTickets,
      ticket: { actually_started: (ticket as any).actually_started ?? null },
    }),
    [derivedTime.requiresRolloverGate, permissions.canAssignTickets, (ticket as any).actually_started],
  );

  const calculateOptiFinish = useMemo(() => {
    const finishDate = derivedTime.optiFinishUtc;
    if (!finishDate || Number.isNaN(finishDate.getTime())) return 'N/A';
    return formatShopTime(finishDate, shopTimezone, 'short');
  }, [derivedTime.optiFinishUtc, shopTimezone]);

  // -----------------------------------------------------------------
  // Slice 5D-11 Batch 1 — advisor-aware bay occupancy estimate.
  //
  // Pure, display-only. Reuses `calculateOptiTime` (Tech OptiTime
  // wall minutes) and the existing ticket fields already on the
  // card. Bay presence is derived from bay_id / parking_spot_id /
  // isInParkingLot / vehiclePresent / status — NO new queries.
  //
  // Batch 1 deliberately does NOT inject observed or predicted
  // advisor delay values on TicketCard: doing so cheaply would
  // require per-ticket custody-event + advisor-bucket queries
  // (N+1 on the Dayboard). The helper handles that case by
  // returning a "≥ Tech OptiTime" lower-bound display with
  // "not enough history yet" wording, ready for Batch 2 to
  // inject board-aggregated values via a parent-level provider.
  //
  // The memo is gated by the flag so OFF-mode pays ZERO cost.
  // -----------------------------------------------------------------
  const bayOccupancyEstimate = useMemo(() => {
    if (!advisorBayOccupancyOnDayboardEnabled) return null;
    const bayPresence = detectVehicleBayPresence({
      // Stage B/F.1: a dependent ticket's bay is a plan, never "in bay".
      bayId: legacyPhysicalBayId(ticket as any),
      parkingSpotId: (ticket as any).parking_spot_id ?? null,
      isInParkingLot: isInParkingLot,
      vehiclePresent: (ticket as any).vehicle_present ?? ticket.vehiclePresent ?? null,
      status: ticket.status,
    });
    const advisorBayHold = calculateAdvisorBayHoldMinutes({
      // Slice 5D-12 Batch 2: real observed / predicted minutes arrive
      // through the `advisorBayHoldData` prop from the board-level
      // provider. When the prop is absent (e.g. other surfaces like
      // BayDetailSheet that haven't been wired yet) we fall back to
      // Batch 1's lower-bound display via `null` inputs.
      observedAdvisorDelayMinutes: advisorBayHoldData?.observedAdvisorDelayMinutes ?? null,
      predictedAdvisorDelayMinutes: advisorBayHoldData?.predictedAdvisorDelayMinutes ?? null,
      bayPresence,
      vehicleExpectedToRemainInBay: bayPresence === 'in_bay',
    });
    // Use the same resolved start instant the existing Tech OptiTime
    // path uses, so Bay Finish lines up with Tech finish when no
    // advisor bay hold is added. displayStartTime > ticket.start_time
    // > null fallback.
    let startInstant: Date | null = null;
    try {
      if (displayStartTime instanceof Date && !Number.isNaN(displayStartTime.getTime())) {
        startInstant = displayStartTime;
      } else {
        const rawDb = (ticket as any).start_time as string | undefined;
        if (rawDb) {
          const parsed = new Date(rawDb);
          if (!Number.isNaN(parsed.getTime())) startInstant = parsed;
        }
      }
    } catch {
      startInstant = null;
    }
    return calculateBayOccupancyEstimate({
      techOptiTimeMinutes: calculateOptiTime,
      advisorBayHold,
      startInstant,
    });
  }, [
    advisorBayOccupancyOnDayboardEnabled,
    calculateOptiTime,
    (ticket as any).bay_id,
    (ticket as any).parking_spot_id,
    (ticket as any).vehicle_present,
    ticket.vehiclePresent,
    ticket.status,
    isInParkingLot,
    displayStartTime,
    (ticket as any).start_time,
    // Slice 5D-12 Batch 2: react to advisor bay-hold values flowing
    // in via the board provider. We track the source + observed +
    // predicted scalars rather than the object identity so transient
    // provider re-runs that return equivalent data don't thrash the
    // memo.
    advisorBayHoldData?.source,
    advisorBayHoldData?.observedAdvisorDelayMinutes,
    advisorBayHoldData?.predictedAdvisorDelayMinutes,
  ]);

  // Validate start time constraints
  const startTimeValidation = useMemo(() => {
    const warnings: string[] = [];
    
    try {
      // Parse start time - if it's a full ISO datetime, use it directly
      let startDate: Date;
      
      if (ticket.startTime.includes('T') || ticket.startTime.includes('Z')) {
        // Full datetime string
        startDate = new Date(ticket.startTime);
      } else {
        // Just a time string, need to get the date from appointmentDate if available
        const timeMatch = ticket.startTime.match(/(\d{1,2}):(\d{2})/);
        if (timeMatch) {
          const [, hours, minutes] = timeMatch;
          // R25-LOW-2: Use shop timezone instead of browser-local setHours
          const shopDateStr2 = getTodayInShopTz(shopTimezone);
          startDate = fromZonedTime(`${shopDateStr2}T${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}:00`, shopTimezone);
        } else {
          startDate = new Date(ticket.startTime);
        }
      }

      // Check if in the past - only show warning if ticket is for today or earlier.
      // Compare via shop-local day labels so advisors in a different browser TZ
      // than the shop still classify "today" correctly.
      const now = clock.now();
      const shopTodayLabel = formatInTimeZone(now, shopTimezone, 'yyyy-MM-dd');
      const ticketShopDayLabel = formatInTimeZone(new Date(ticket.startTime), shopTimezone, 'yyyy-MM-dd');

      if (ticketShopDayLabel === shopTodayLabel && startDate < now) {
        warnings.push('Start time is in the past');
      }

      // Check technician shift hours - use the hours for the specific day if available
      if (technician) {
        const tech = (ticket as any).technician;
        let shiftStart: string | undefined;
        let shiftEnd: string | undefined;
        
        if (tech?.hours) {
          const dayOfWeek = toZonedTime(startDate, shopTimezone).getDay();
          // Support both numeric and string keys
          const dayHours = tech.hours[dayOfWeek] || tech.hours[dayOfWeek.toString()];
          if (dayHours?.open && dayHours?.close) {
            shiftStart = dayHours.open;
            shiftEnd = dayHours.close;
          }
        }
        
        // Fallback to default shift times
        if (!shiftStart && technician.shiftStart) {
          shiftStart = technician.shiftStart;
        }
        if (!shiftEnd && technician.shiftEnd) {
          shiftEnd = technician.shiftEnd;
        }
        
        if (shiftStart && shiftEnd) {
          try {
            const [startH, startM] = shiftStart.split(':').map(Number);
            const [endH, endM] = shiftEnd.split(':').map(Number);
            
            const shiftStartTime = startH * 60 + startM;
            const shiftEndTime = endH * 60 + endM;
            const ticketStartTime = getMinutesInShopTZ(startDate.toISOString(), shopTimezone);
            
            // FIXED: Don't show warning - breaks push tickets beyond shift boundaries naturally
            // The smart-assign already validated this ticket is legal
            // This warning was causing confusion for tickets scheduled after breaks
          } catch (e) {
            console.error('Error parsing shift times:', e);
          }
        }
      }
    } catch {
      // Ignore parsing errors
    }

    return warnings;
  }, [ticket.startTime, technician]);


  // REMOVED: updateTicketStatus was dead code that bypassed the canonical
  // completion path. That path is now `completeTicketIfEligible`; the
  // `stopTicketTimer` funnel it originally named was deleted in 8F.

  // REMOVED: handlePause, handlePauseConfirm, handlePlayClick, handleServiceInfoComplete,
  // handlePlay, handleComplete, handleConfirmedStop — all dead code that bypassed the canonical
  // ticketTimerManager funnel. Live timer UI is TicketTimerControls (rendered below).
  // See Round 37, FIX 2.

  const completeTicketAndParts = async (
    location?: { kind: 'PARKING_SPOT' | 'GENERAL_LOT'; spotId: string | null }) => {
    // If this is a comeback ticket and no resolution is set, open modal — for the FRONT OFFICE only. OD-46 (2026-10-15):
    // a technician cannot attribute a comeback (tech_fault / parts_failure / unrelated moves demonstrated proficiency);
    // the server does not apply a technician's write, so their completion proceeds and the front office records it.
    if ((ticket as any).is_comeback && !(ticket as any).comeback_resolution && permissions.canEditTickets) {
      setPendingCompletionTicket(ticket);
      setShowComebackModal(true);
      return;
    }

    // Check for diagnostic/inspection jobs that need outcome capture
    await checkAndShowDiagnosticModal(location);
  };

  const checkAndShowDiagnosticModal = async (
    location?: { kind: 'PARKING_SPOT' | 'GENERAL_LOT'; spotId: string | null }) => {
    try {
      // Use the shared utility to get unresolved diagnostic jobs
      const unresolvedJobs = await getJobsNeedingOutcomeForModal(ticket.id);

      if (unresolvedJobs.length > 0) {
        // No role may author it here any more — see the note on
        // handleDiagnosticOutcome. Both roles now take the same path: the
        // requirement is surfaced and the server refuses the completion.
        {
          // Technician: Set the flag and proceed with completion (no modal)
          // NOTE: this sets a flag and proceeds. The flag is a UX marker,
          // not a resolution — the server now refuses this ticket with
          // DIAGNOSTIC_REQUIRED regardless, so the technician is told the
          // truth instead of the flag standing in for an answer.
          await setDiagnosticOutcomeFlagOnCompletion(ticket.id);
          await performCompletion(location);
          return;
        }
      }

      // No diagnostic jobs or all have outcomes, proceed with completion
      await performCompletion(location);
    } catch (error) {
      // FAIL CLOSED. This used to fall through to performCompletion(), so a
      // failed preflight — a network blip, an RLS refusal — became a silent
      // licence to end the visit without the outcome it was checking for.
      // The server refuses this case on its own now, but a client that
      // reacts to "I could not check" by doing the thing anyway is wrong
      // regardless of who catches it.
      console.error('Error checking diagnostic jobs:', error);
      toast({
        title: 'Could not check diagnostic outcomes',
        description: 'Nothing was completed. Please try again.',
        variant: 'destructive',
      });
    }
  };

  /**
   * The confirmed merge. The structural decision is the SERVER's: this reads
   * the authority's named outcome and turns it into something an operator can
   * act on. It never decides whether the merge is legal.
   */
  const handleConfirmMerge = async () => {
    if (isMerging) return;          // a second confirm click is not a second act
    setIsMerging(true);
    try {
      const mergedTicketId = await mergeSplitTickets(ticket.id);
      toast({
        title: 'Split parts merged',
        description: `The parts are now one ticket (${mergedTicketId}).`,
      });
      setShowMergeConfirm(false);
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      queryClient.invalidateQueries({ queryKey: ['parking-lot-tickets'] });
      void notifyDayboardInvalidated({
        reason: 'structural-merge',
        shopId: (ticket as any).shop_id || '',
        ticketId: ticket.id,
        metadata: { kind: 'merge' },
      });
    } catch (error: any) {
      // The authority's refusals already carry operator-readable copy; this
      // surfaces it rather than inventing a second explanation.
      toast({
        title: 'Merge not completed',
        description: error?.message ?? 'The merge could not be completed.',
        variant: 'destructive',
      });
      setShowMergeConfirm(false);
      // Whatever refused, the board's picture of this family may be stale.
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
    } finally {
      setIsMerging(false);
    }
  };

  /**
   * Report that the diagnostic competency outcome is still owed.
   *
   * CORE-I: this surface may say the competency evidence is MISSING; it
   * may not supply it. The outcome is the performing technician's own
   * assertion, made on the authenticated diagnostic-capture path, and a
   * completion actor cannot be shown to be that person. Writing it here
   * is refused by `core_i_competency_writer_trg`, and completion is
   * already fail-closed server-side with DIAGNOSTIC_REQUIRED.
   */
  const reportDiagnosticOutcomeRequired = async () => {
    setShowDiagnosticModal(false);
    try {
      toast({
        title: 'Diagnostic outcome still required',
        description:
          'The technician who performed the diagnostic work records what it '
          + 'found, from their timer. This ticket cannot be completed until '
          + 'they do.',
        variant: 'destructive',
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to save diagnostic outcomes',
        variant: 'destructive',
      });
    } finally {
      setPendingDiagnosticJobs([]);
    }
  };

  // The badge reports what is outstanding. It no longer opens an authoring
  // surface, for the reason above.
  const handleDiagnosticBadgeClick = async () => {
    const unresolvedJobs = await getJobsNeedingOutcomeForModal(ticket.id);
    if (unresolvedJobs.length > 0) {
      await reportDiagnosticOutcomeRequired();
    }
  };

  const handleComebackResolution = async (resolution: 'parts_failure' | 'tech_fault' | 'unrelated') => {
    setShowComebackModal(false);
    
    if (!pendingCompletionTicket) return;

    try {
      // Save the comeback resolution
      const { error: resolutionError } = await supabase
        .from('tickets')
        .update({ comeback_resolution: resolution })
        .eq('id', pendingCompletionTicket.id);

      if (resolutionError) throw resolutionError;

      // The proficiency effect is NOT applied from here any more.
      //
      // Writing the resolution IS the act. `core_e_comeback_resolution_trg`
      // carries it to the demonstrated proficiency authority in the same
      // transaction that records it, so the attribution and its consequence
      // cannot come apart — which they previously could, because this was a
      // second client call whose failure was swallowed and whose absence
      // nothing would ever have noticed.
      //
      // It also covers what this branch never did: parts_failure and
      // unrelated are now recorded as looked-at-and-correctly-ignored, and a
      // corrected attribution reverses exactly.

      // Check for diagnostic jobs before completing, carrying the location
      // the human already asserted.
      await checkAndShowDiagnosticModal(pendingCompletionLocation ?? undefined);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to save comeback resolution',
        variant: 'destructive',
      });
    } finally {
      setPendingCompletionTicket(null);
    }
  };

  const performCompletion = async (
    location?: { kind: 'PARKING_SPOT' | 'GENERAL_LOT'; spotId: string | null }) => {
    setIsUpdating(true);
    try {
      // TIMER V2 LIFECYCLE CUTOVER.
      // This action's intent is COMPLETE TICKET, so it now routes to the
      // explicit completion authority instead of the old `stopTicketTimer`
      // funnel. The funnel completed every job on the ticket in order to
      // justify sealing it; completion is now the CONSEQUENCE of finished
      // work, and the authority REFUSES when work remains rather than
      // manufacturing eligibility.
      // 8G.1: completion now carries the human's LOCATION assertion and the
      // server commits both as one statement. `completeTicketAndParts` is
      // reached only from `submitCompletionWithLocation`, which has already
      // collected that answer — the terminal CHECK makes "complete now, park
      // later" impossible, so there is no path that completes without it.
      // The argument wins over state: `submitCompletionWithLocation` sets
      // state for retry UX and calls straight through in the same tick, and
      // a state read here would still see the PREVIOUS value.
      const loc = location ?? pendingCompletionLocation;
      if (!loc) {
        // Unreachable through the UI, which collects the answer first. Kept
        // because the alternative to an explicit refusal here is inventing a
        // location, and that is exactly what must never happen.
        toast({
          title: 'Where did the vehicle go?',
          description: 'Choose a parking spot or the general lot to finish.',
          variant: 'destructive',
        });
        return;
      }
      const completion = await completeTicketWithLocation(
        ticket.id, loc.kind, loc.spotId);

      if (!isCompletionSettled(completion.outcome)) {
        toast({
          title: completion.outcome === 'LOCATION_CONFLICT'
            ? 'That space is not available'
            : 'Not ready to complete',
          description: describeCompletionRefusal(completion),
          variant: 'destructive',
        });
        return;
      }

      // 8F.1: this used to say the post-completion side effects (split sibling
      // adjustment, efficiency learning, cascade, rollover prompting, parking
      // spot prompting) were "handled by stopTicketTimer". That funnel was
      // deleted in 8F and its side effects were deliberately NOT migrated —
      // they are TERMINAL_LIFECYCLE_DEFERRED and belong to the closeout phase.
      // Do not read this as a claim that they still run.

      // A location asserted for THIS ticket must not survive into the next
      // completion on this card.
      setPendingCompletionLocation(null);
      setCompletionLocationOpen(false);

      // Realtime usually delivers this, but "usually" is not a refresh
      // policy: the operator has just ended a visit and must see it move.
      void notifyDayboardInvalidated({
        reason: 'ticket-completed',
        shopId: (ticket as any).shop_id || activeShop?.id || '',
        ticketId: ticket.id,
      });

      toast({
        title: 'Status Updated',
        description: `Ticket ${ticket.id} is now Completed`,
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to complete ticket',
        variant: 'destructive',
      });
    } finally {
      setIsUpdating(false);
    }
  };

  /**
   * THE MANUAL COMPLETION SURFACE.
   *
   * 8G.1 removed the effect that completed a ticket automatically and left
   * nothing in its place: `readyToComplete`, `pendingCompletionLocation` and
   * `completionLocationOpen` were all declared with no consumer and no
   * setter caller, so completion was unreachable from this card entirely.
   *
   * The order matters and is the whole point. The location is collected
   * FIRST, because the terminal CHECK forbids a completed ticket that still
   * holds a bay — "complete now, park later" is not merely discouraged, it
   * is rejected at statement one. Obligations are resolved next, and only
   * then does one call go to the authority.
   */
  const submitCompletionWithLocation = async (spotId: string | null) => {
    const location: { kind: 'PARKING_SPOT' | 'GENERAL_LOT'; spotId: string | null } =
      spotId ? { kind: 'PARKING_SPOT', spotId } : { kind: 'GENERAL_LOT', spotId: null };
    setCompletionLocationOpen(false);
    // Held so a LOCATION_CONFLICT retry does not re-ask, and cleared on
    // success, on cancel, and whenever the card's ticket changes.
    setPendingCompletionLocation(location);
    await completeTicketAndParts(location);
  };

  const handleLocalJobAdded = (job: any) => {
    setTicketJobs((prev: any[]) => (prev.some((j) => j.id === job.id) ? prev : [...prev, job]));
    onTicketUpdated?.();
  };

  const handleDeleteJob = async (jobId: string) => {
    if (!permissions.canEditTickets) return;
    const prev = ticketJobs;
    const deletedJob = ticketJobs.find((j) => j.id === jobId);
    
    if (!deletedJob) {
      toast({
        title: 'Error',
        description: 'Job not found',
        variant: 'destructive',
      });
      return;
    }
    // oct20e (FD-2026-10-24-C): a ticket keeps at least one job; the server refuses removing the last (TICKET_REQUIRES_JOB).
    if (ticketJobs.length <= 1) {
      toast({ title: 'A ticket needs at least one job', description: 'Add the replacement job first, then remove this one.', variant: 'destructive' });
      return;
    }
    
    setTicketJobs((jobs) => jobs.filter((j) => j.id !== jobId));
    
    try {
      // Get current ticket data
      const { data: ticketData, error: ticketError } = await supabase
        .from('tickets')
        .select('predicted_minutes, assigned_tech_id, start_time, scheduled_date')
        .eq('id', ticket.id)
        .single();

      if (ticketError) throw ticketError;

      // Delete the job from ticket_jobs
      const { error: deleteError } = await supabase
        .from('ticket_jobs')
        .delete()
        .eq('ticket_id', ticket.id)
        .eq('job_id', jobId);

      if (deleteError) throw deleteError;


      // For the confirmation toast only (the write is owned elsewhere).
      const jobMinutes = deletedJob.standardMinutes || 0;
      const newPredictedMinutes = Math.max(0, ticketData.predicted_minutes - jobMinutes);

      // Recalculate has_teardown_jobs and earliest_parts_eta rollups
      const { data: remainingJobs } = await supabase
        .from('ticket_jobs')
        .select('teardown_enabled, parts_eta')
        .eq('ticket_id', ticket.id);

      const hasTeardownJobs = remainingJobs?.some(tj => tj.teardown_enabled) || false;
      
      // Find earliest parts ETA from remaining jobs
      let earliestPartsEta = null;
      if (remainingJobs) {
        const etaDates = remainingJobs
          .map(tj => tj.parts_eta)
          .filter(eta => eta !== null)
          .map(eta => new Date(eta!));
        
        if (etaDates.length > 0) {
          earliestPartsEta = new Date(Math.min(...etaDates.map(d => d.getTime()))).toISOString();
        }
      }

      // Get current user for tracking
      const { data: { user } } = await supabase.auth.getUser();

      // The job line is gone: re-sum the ticket's time from its remaining
      // lines (minutes_override ?? standard_minutes) through the canonical
      // helper. ROLLOVER CHAIN: on a chain row the helper writes no local sum
      // and asks the chain authority to re-plan (unchanged behaviour); on an
      // ordinary ticket it writes predicted_minutes, which scheduling and
      // capacity read (field test 2026-10-20, FD-13 — the total used to go stale).
      try {
        const { recomputeTicketPredictedMinutes } = await import('@/utils/recomputeTicketPredictedMinutes');
        await recomputeTicketPredictedMinutes(ticket.id, { userId: user?.id, skipCascade: true });
      } catch (chainErr) {
        console.error('Failed to recompute the ticket time after job removal:', chainErr);
      }

      // Update ticket side-fields (no predicted_minutes)
      await supabase
        .from('tickets')
        .update({ 
          has_teardown_jobs: hasTeardownJobs,
          earliest_parts_eta: earliestPartsEta,
          last_edited_by_user_id: user?.id,
          last_edited_at: clock.now().toISOString(),
        })
        .eq('id', ticket.id);

      // Trigger schedule recalculation/cascade if ticket is assigned
      if (ticketData.assigned_tech_id) {
        try {
          const dateStr = ticketData.scheduled_date ?? getShopDayLabel(ticketData.start_time, shopTimezone);
          const { triggerBoardCascade } = await import('@/utils/cascadeTrigger');
          const ticketShopId = (ticket as any).shop_id || activeShop?.id;
          if (ticketShopId) {
            await triggerBoardCascade(ticketShopId, dateStr, ticketData.assigned_tech_id);
          }
        } catch (cascadeError) {
          console.error('Failed to trigger cascade after job deletion:', cascadeError);
          toast({ title: '⚠️ Schedule may need refresh', description: 'Automatic recalculation failed.' });
        }
      }

      toast({
        title: 'Job Removed',
        description: `Job removed. Ticket time updated: ${newPredictedMinutes} minutes`,
      });
      onTicketUpdated?.();
    } catch (error: any) {
      setTicketJobs(prev);
      toast({
        title: 'Error',
        description: error.message || 'Failed to remove job',
        variant: 'destructive',
      });
    }
  };

   const handleEditJob = async (hours: number) => {
    if (!editingJob) return;
    // R40 FIX-3: Permission gate (mirrors handleDeleteJob at line 1276)
    if (!permissions.canEditTickets) {
      toast({
        title: 'Not permitted',
        description: 'You do not have permission to edit job times.',
        variant: 'destructive',
      });
      return;
    }
    
    const newMinutes = Math.round(hours * 60);
    const previousMinutes = Math.round(editingJob.hours * 60);
    
    try {
      
      const { error } = await supabase
        .from('ticket_jobs')
        .update({ 
          minutes_override: newMinutes,
        })
        .eq('ticket_id', ticket.id)
        .eq('job_id', editingJob.id);
      
      if (error) throw error;

      // Phase 10 R2: refresh labor sell on this row (preserves override; no-op if uncomputed or flags off).
      try {
        const ticketShopIdForLabor = (ticket as any).shop_id || activeShop?.id;
        if (ticketShopIdForLabor) {
          await refreshLaborForRowEdit({
            ticketId: ticket.id,
            jobId: editingJob.id,
            shopId: ticketShopIdForLabor,
            effectiveMinutes: newMinutes,
            flags: {
              labor_engine_enabled: laborEngineEnabled,
              ui_ticket_financials: ticketFinancialsEnabled,
            },
          });
          queryClient.invalidateQueries({ queryKey: ['ticket-labor-lines', ticket.id] });
          // Phase 10 R3: refresh tech-pay snapshot. No-op when flags off, no
          // assigned tech, or tech_pay_status IS NULL (strict no-create-on-edit).
          await refreshTechPayForRowEdit({
            ticketId: ticket.id,
            jobId: editingJob.id,
            shopId: ticketShopIdForLabor,
            referenceMinutes: newMinutes,
            flags: {
              labor_engine_enabled: laborEngineEnabled,
              ui_technician_pay: techPayEnabled,
            },
          });
          queryClient.invalidateQueries({ queryKey: ['ticket-tech-pay-lines', ticket.id] });
        }
      } catch (laborErr) {
        console.error('[handleEditJob] Failed to refresh labor sell / tech pay:', laborErr);
      }

      toast({
        title: 'Job Updated',
        description: `${editingJob.name} updated successfully`,
      });

      // Log the job time edit event (fire-and-forget)
      if (activeShop) {
        const { logJobTimeEdited } = await import('@/utils/ticketEventLogger');
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          logJobTimeEdited({
            ticketId: ticket.id,
            shopId: activeShop.id,
            userId: user.id,
            jobName: editingJob.name,
            previousMinutes,
            newMinutes,
          }).catch(err => console.error('[handleEditJob] Failed to log event:', err));
        }
      }
      
       setEditingJob(null);

      // The FOH-entered job time is the ticket's time (FD-13): re-sum
      // predicted_minutes from minutes_override ?? standard_minutes through the
      // canonical helper, so scheduling / capacity / OptiPlace read the edit.
      // ROLLOVER CHAIN: on a chain row the helper writes no local sum and asks
      // the chain authority to re-derive every share (unchanged behaviour).
      try {
        const { recomputeTicketPredictedMinutes } = await import('@/utils/recomputeTicketPredictedMinutes');
        const { data: { user: editor } } = await supabase.auth.getUser();
        await recomputeTicketPredictedMinutes(ticket.id, { userId: editor?.id, skipCascade: true });
      } catch (tailErr) {
        console.error('[handleEditJob] Failed to recompute the ticket time:', tailErr);
      }

      // Trigger board cascade after edit-job wall-time mutation (Chronomancer compliance)
      try {
        const ticketShopId = (ticket as any).shop_id || activeShop?.id;
        const dateStr = (ticket as any).scheduled_date ?? getShopDayLabel(ticket.startTime, shopTimezone);
        if (ticketShopId) {
          await triggerBoardCascade(ticketShopId, dateStr, ticket.assignedTechId || undefined);
        }
      } catch (cascadeErr) {
        console.error('[handleEditJob] Failed to trigger cascade:', cascadeErr);
      }

      // R39 FIX-5: Invalidate queries AFTER DB writes + cascade to prevent stale refetch
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      queryClient.invalidateQueries({ queryKey: ['parking-lot-tickets'] });
      queryClient.invalidateQueries({ queryKey: ['ticket-jobs'] });

      onTicketUpdated?.();
    } catch (error: any) {
      // R40 FIX-7: Revert predicted_minutes on failure.
      //
      // CSI-I2-A5: route the revert through the canonical
      // `mutateTicketSchedule` helper so `predicted_minutes` writes go
      // through the same allowlist gate the success path uses (the
      // success path never writes a chain row's minutes — the rollover
      // chain authority derives them from the job ledger). skipCascade:true
      // mirrors the prior raw-write behavior — the success path's
      // post-update cascade runs only on the happy path; the revert
      // is a compensating local-state write that should not fan out
      // and disturb downstream tickets that may already be reading
      // the failed-write's effect.
      try {
        // A chain row is never "reverted" by subtracting a delta: its share is
        // whatever the chain's job ledger says, so re-derive it instead.
        const { recomputeRolloverChainWork } = await import('@/utils/rolloverChain');
        const chainHandled = await recomputeRolloverChainWork(ticket.id);
        const { data: currentTicket } = chainHandled ? { data: null } : await supabase
          .from('tickets')
          .select('predicted_minutes')
          .eq('id', ticket.id)
          .single();
        if (currentTicket) {
          const revertedPredicted= Math.max(0, (currentTicket.predicted_minutes || 0) - (newMinutes - previousMinutes));
          const revertResult = await mutateTicketSchedule(
            ticket.id,
            { predicted_minutes: revertedPredicted },
            {
              reason: 'ticket_card_edit_job_revert_predicted_minutes',
              skipCascade: true,
            },
          );
          if (revertResult.success) {
            console.warn('[handleEditJob] Reverted predicted_minutes after failure');
          } else {
            console.error('[handleEditJob] Failed to revert predicted_minutes:', revertResult.error);
          }
        }
      } catch (revertErr) {
        console.error('[handleEditJob] Failed to revert predicted_minutes:', revertErr);
      }
      toast({
        title: 'Error',
        description: error.message || 'Failed to update job',
        variant: 'destructive',
      });
    }
  };

  // Check if ticket is split or rollover
  const isSplit = isTicketSplit(ticket.id);
  const isRollover =
    (ticket as any).split_reason === 'EOD_ROLLOVER' ||
    !!(ticket as any).parent_ticket_id ||
    !!(ticket as any).rollover_of_ticket_id;
  
  // Hide timer controls for service writers
  const shouldHideTimerControls = hideTimerControls || !permissions.canControlTimers;

  // ---------------------------------------------------------------------
  // PTP-012 — WHEN THE TIMER SURFACE IS TRUTHFUL, from the CANONICAL model.
  //
  // This block invented its own location model twice, and was wrong both times.
  //
  //   v1 `ticket.assignedTechId` — but `tickets_parking_lot_immunity` FORCES
  //      `assigned_tech_id` NULL on every parked ticket, so the entire parked
  //      population lost its timer surface as a side effect of a constraint
  //      about assignment.
  //
  //   v2 `parking_lot === true` read as "the general lot" — but the canonical
  //      writer `apply_vehicle_location_locked` writes a NUMBERED SPOT as
  //      `bay_id = NULL, parking_spot_id = <chosen>, parking_lot = TRUE`, and
  //      the general lot as the same row without the spot. `parking_lot` means
  //      "in the parking area", not "no specific spot". Observed at Floyd's as
  //      a card showing spot 002 with no Start at all.
  //
  // The classification lives in `parkingToBayTimerStart.ts` and is
  // precedence-ordered. The card asks THAT, so the UI and the start authority
  // answer location from one model instead of three.
  // ---------------------------------------------------------------------
  const timerLocationShape = {
    status: ticket.status,
    deleted_at: (ticket as any).deleted_at ?? null,
    archived_at: (ticket as any).archived_at ?? null,
    picked_up_at: (ticket as any).picked_up_at ?? null,
    completed_at: (ticket as any).completed_at ?? null,
    paused_at: (ticket as any).paused_at ?? null,
    pause_reason: ticket.pauseReason ?? null,
    parking_lot: (ticket as any).parking_lot ?? null,
    parking_spot_id: (ticket as any).parking_spot_id ?? null,
    bay_id: (ticket as any).bay_id ?? null,
    timer_status: localTimerState.timer_status ?? (ticket as any).timer_status ?? null,
    actually_started: (ticket as any).actually_started ?? null,
    is_estimate: (ticket as any).is_estimate ?? null,
    assigned_tech_id: ticket.assignedTechId ?? null,
    scheduled_tech_id: (ticket as any).scheduled_tech_id ?? null,
    scheduled_date: (ticket as any).scheduled_date ?? null,
  };
  const timerLocation = getTimerStartLocationState(timerLocationShape);
  const timerNeedsPullIn = requiresBayConfirmationBeforeTimerStart(timerLocationShape);

  // Present AND either already in a bay (ordinary Start), or parked in a state
  // the canonical pull-in covers. `pull_in_and_start_my_timer` is the authority
  // on WHO may actually do it — the card only decides whether to offer the act,
  // and the server refuses anyone it should.
  const timerSurfaceIsStartable =
    localVehiclePresent === true && (timerLocation === 'in_bay' || timerNeedsPullIn);
  
  // OCT28 #8: ONE run state for both borders, from the authoritative timer projection (timer_status). Running and
  // paused outrank rollover (a running continuation is running); a started ticket whose timer is stopped is not green.
  const cardRunState = cardTimerRunState({
    timerStatus: localTimerState.timer_status,
    filmOverride: !!(isMarketingMode && getTimerOverride((ticket as any).customer_name || ticket.customerName)),
    isRollover,
  });
  // OCT28 RECOVERY #8 (founder audit 2026-11-02): the card has ONE border authority, getCardBorderClass below. The
  // inline top border used to repaint the top edge from `ticket.status` (Active = blue) over the run-state colour, so a
  // paused rollover showed an amber/orange frame with a blue top; the never-called left-stripe helper is gone too.
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  // Get border class based on status
  const getCardBorderClass = () => {
    const confirmationStatus = (ticket as any).confirmation_status;
    
    // Confirmation-required tickets (Lane 3A/3B) get pulsing amber/magenta border
    if (confirmationStatus === 'needs_contact') {
      return "border-4 border-amber-500 animate-pulse-confirmation";
    }
    if (confirmationStatus === 'contacted') {
      return "border-4 border-amber-500"; // Solid amber, no pulse
    }
    
    if (cardRunState === 'running') return "border-4 border-green-500";
    if (cardRunState === 'paused') return "border-4 border-yellow-600";
    if (cardRunState === 'rollover') return "border-4 border-[hsl(var(--status-rollover))]";
    return "border-2 border-border"; // Default border
  };

  return (
    <Card
      id={`ticket-${ticket.id}`}
      data-testid={`ticket-card-${ticket.id}`}
      ref={setNodeRef}
      style={style}
        className={cn(
          "relative overflow-hidden transition-all hover:shadow-md",
          isDragging && "opacity-50 shadow-xl scale-105",
          isNewOptiQueue && "border-4 animate-flash-red-border",
          !isNewOptiQueue && getCardBorderClass(),
          isHighlighted && "animate-pulse-border",
          isPendingMove && "opacity-60 border-2 border-dashed border-primary bg-primary/5 animate-pulse"
        )}
    >
      {/* Red header for non-present vehicles */}
      {!localVehiclePresent && (
        <div
          className="bg-destructive text-destructive-foreground px-4 py-2 text-sm font-semibold cursor-pointer hover:bg-destructive/90 transition-colors"
          onDoubleClick={() => setIsCheckInDialogOpen(true)}
          title="Double-click to confirm vehicle check-in"
        >
          🔒 Ticket locked - vehicle not present (Double-click to unlock)
        </div>
      )}
      <div 
        className={cn("p-4", isInParkingLot && "cursor-pointer hover:bg-accent/50 transition-colors")}
        onClick={() => isInParkingLot && setIsCollapsed(!isCollapsed)}
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            {localVehiclePresent && !hideKebabMenu && (
              <div className="absolute top-2 right-2 flex items-center gap-1">
                <TicketKebabMenu
                  ticketId={ticket.id}
                  isInParkingLot={isInParkingLot}
                  technicians={technicians}
                  assignedTechId={ticket.assignedTechId}
                  jobCount={ticketJobs.length}
                  isSplitTicket={!!(ticket as any).split_group_id}
                  status={ticket.status}
                  vehiclePresent={ticket.vehiclePresent}
                  ticketType={ticket.type}
                  scheduledStartTime={(ticket as any).scheduled_start_time}
                  dueOut={(ticket as any).due_out}
                  isComeback={ticket.isComeback}
                  predictedMinutes={ticket.predictedMinutes}
                  needsDiagnosticOutcome={(ticket as any).needs_diagnostic_outcome}
                  confirmationStatus={(ticket as any).confirmation_status}
                  teamId={(ticket as any).team_id}
                  onAddJob={() => setIsAddJobDialogOpen(true)}
                  onDelete={onDelete}
                  onSplit={() => setIsManualSplitModalOpen(true)}
                  onMerge={() => setShowMergeConfirm(true)}
                  onReassign={onReassign}
                />
                {isDraggable && (
                  <div
                    {...attributes} 
                    {...listeners}
                    className="cursor-grab active:cursor-grabbing p-1 rounded hover:bg-muted/50 transition-colors"
                  >
                    <GripVertical className="h-4 w-4 text-muted-foreground" />
                  </div>
                )}
              </div>
            )}
            {/* Oct 20 addendum L1: no full menu on a read-only card — Move Vehicle only, for a viewer who may relocate. */}
            {localVehiclePresent && hideKebabMenu && (
              <div className="absolute top-2 right-2 flex items-center gap-1">
                <TicketRelocateOnlyMenu ticketId={ticket.id} status={ticket.status} />
              </div>
            )}
            
            {/* Type and Status Badges - Above Customer Name */}
            <div className="flex items-center gap-1 mb-1.5 flex-wrap">
              {ticketAdvisor?.name && <AdvisorInitialsBadge userId={ticketAdvisor.userId} name={ticketAdvisor.name} />}
              {bayName && (
                <Badge 
                  variant="outline" 
                  className="text-xs bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800 cursor-pointer hover:bg-blue-500/20 transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/birdseye?moveTicketId=${ticket.id}`);
                  }}
                  title="Click to change bay"
                >
                  {/* Stage B/F.1: a dependent ticket's bay is where it is
                      PLANNED to go, not where the car is. */}
                  {isDependentTicket(ticket as any) ? `Planned: ${bayName}` : bayName}
                </Badge>
              )}
              {isSplit && <RolloverBadge type="split" className="text-xs" />}
              {isRollover && <RolloverBadge type="rollover" className="text-xs" />}
              {(ticket as any).auto_created && <RolloverBadge type="auto-rolled" className="text-xs" />}
              {((ticket as any).split_reason === 'EOD' || (ticket as any).split_reason === 'EOD_ROLLOVER') && <Badge variant="outline" className="text-xs bg-amber-500/10 text-amber-700 dark:text-amber-300">EOD Split</Badge>}
              {(ticket as any).split_group_id && (
                <SplitLink
                  splitParentId={(ticket as any).split_parent_id}
                  splitGroupId={(ticket as any).split_group_id}
                  splitSequence={(ticket as any).split_sequence}
                  rolloverOfTicketId={(ticket as any).rollover_of_ticket_id}
                  splitLabelData={splitLabelData}
                />
              )}
              <Badge variant={ticket.type === 'Waiter' ? 'destructive' : 'secondary'} className="text-xs">
                {ticket.type}
              </Badge>
              {custodyHandoffEnabled && (
                <TicketCustodyBadge
                  custody={custodyRow ?? null}
                  onAcceptBoh={handleAcceptBackFromCard}
                />
              )}
              {ticket.status === 'Completed' && (
                <Badge variant="outline" className="text-xs bg-green-500/10 text-green-700 dark:text-green-300">
                  Completed
                </Badge>
              )}
              {ticket.priority === 'High' && (
                <Badge variant="outline" className="text-xs">High Priority</Badge>
              )}
              {(ticket as any).is_after_hours && (
                <Badge variant="secondary" className="text-xs gap-1">
                  <Moon className="h-3 w-3" />
                  After Hours
                </Badge>
              )}
              <SpecialtyBadges 
                requiresAlignment={(ticket as any).requires_alignment}
                requiresAdas={(ticket as any).requires_adas}
              />
              <TeamBadge teamId={(ticket as any).team_id} size="sm" />
              <ReminderStatusBadge ticketId={ticket.id} />
              {/* Core-100 R4 (CORE-AZ): the customer's reply, as theirs; no reply shows nothing */}
              <CustomerReplyBadge customerConfirmed={(ticket as { customer_confirmed?: boolean | null }).customer_confirmed} />
              <DiagnosticOutcomeBadge 
                needsDiagnosticOutcome={(ticket as any).needs_diagnostic_outcome}
                onClick={handleDiagnosticBadgeClick}
              />
              {/* Time Turner Confirmation Badge */}
              {(ticket as any).confirmation_status === 'needs_contact' && (
                <Badge 
                  variant="outline" 
                  className="text-xs bg-amber-500/20 text-amber-700 dark:text-amber-300 border-amber-400 animate-pulse gap-1"
                >
                  <AlertCircle className="h-3 w-3" />
                  Needs Confirmation
                </Badge>
              )}
              {(ticket as any).confirmation_status === 'contacted' && (
                <Badge 
                  variant="outline" 
                  className="text-xs bg-amber-500/10 text-amber-600 border-amber-300 gap-1"
                >
                  <Clock className="h-3 w-3" />
                  Contacted - Pending
                </Badge>
              )}
              {serverEod?.verdict.state === 'unconfirmed' && (
                <div className="text-[10px] text-amber-700 dark:text-amber-300" data-testid="eod-check-unconfirmed">
                  {describeUnconfirmed(serverEod.verdict, (d) => formatTimeInShopTZ(d))}
                </div>
              )}
              {serverEod?.verdict.state === 'conflict' && (
                <div className="text-[10px] text-red-700 dark:text-red-300" data-testid="eod-conflict">
                  {describeEodConflict(serverEod.verdict.overrunMin, serverEod.presentation)}
                </div>
              )}
              {ticket.status === 'Paused' && (ticket as any).pause_reason && (
                <PauseReasonBadge 
                  ticketId={ticket.id} 
                  pauseReason={(ticket as any).pause_reason} 
                />
              )}
              {/* Picked Up - Incomplete Badge */}
              {ticket.status === 'Paused' && (ticket as any).picked_up_at && !(ticket as any).completed_at && (
                <Badge 
                  variant="outline" 
                  className="text-xs bg-destructive/10 text-destructive border-destructive/30 gap-1"
                >
                  <AlertTriangle className="h-3 w-3" />
                  Picked Up - Incomplete
                </Badge>
              )}
              {/* Parking Spot Badge */}
              {parkingSpotLabel && (
                <Badge variant="secondary" className="text-xs gap-1">
                  <ParkingSquare className="h-3 w-3" />
                  {parkingSpotLabel.replace(/^Parking\s*/i, 'Parking Spot ')}
                </Badge>
              )}
              {/*
                Parked-but-scheduled fallback badge.
                When a ticket has parking_lot=true AND assigned_tech_id=NULL
                (the canonical post-check-in state — required by the
                `tickets_parking_lot_immunity` constraint) AND a planned
                tech is recorded in scheduled_tech_id (added in migration
                20260601160000), surface the dispatch plan so the card
                doesn't look unassigned. TechnicianColumn keeps these
                tickets in the planned tech's column; this badge makes
                the parked-not-actively-worked state explicit and also
                informs the Parking Lot section where multiple techs'
                tickets are mixed.
              */}
              {(ticket as any).parking_lot === true &&
                (ticket.assignedTechId == null) &&
                (ticket as any).scheduled_technician?.name && (
                <Badge variant="outline" className="text-xs gap-1">
                  Parked — scheduled for {(ticket as any).scheduled_technician.name}
                </Badge>
              )}
            </div>

            {/* Ticket Number - Click to open Details Modal */}
            <span
              data-testid="ticket-number"
              className="text-xs text-muted-foreground font-mono cursor-pointer hover:text-primary hover:underline transition-colors mb-1 inline-block"
              onClick={(e) => {
                e.stopPropagation();
                setDetailsTarget(null);
                setIsDetailsModalOpen(true);
              }}
              title="Open ticket details"
            >
              {/* NOV3 A6: one repair = one number — every day of a multi-day repair shows the head's number. */}
              {formatTicketNumber(ticket as any)}
            </span>
            {/* Oct 20 Issue 06 (FD-09): Notes shortcut — every Core role; opens this ticket's Command Center on the
                Workflow tab with Internal Notes (the whole rollover chain's notes) brought into view. No count badge. */}
            <button
              type="button"
              data-testid="ticket-notes-shortcut"
              aria-label="Open notes"
              title="Open notes"
              className="ml-1 mb-1 inline-flex h-8 w-8 items-center justify-center rounded-md align-middle text-muted-foreground hover:bg-muted hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring [@media(pointer:coarse)]:h-11 [@media(pointer:coarse)]:w-11"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                setDetailsTarget({ tab: 'workflow', section: 'notes' });
                setIsDetailsModalOpen(true);
              }}
            >
              <StickyNote className="h-4 w-4" aria-hidden="true" />
            </button>

            {/* Customer Name and Ticket ID */}
            <div className="flex items-center justify-between mb-1" data-testid="ticket-customer-info">
              <div className="flex items-center gap-2">
                {ticket.customerName ? (
                  <span className="font-bold text-sm" data-testid="ticket-customer-name">
                    {ticket.customerName}
                  </span>
                ) : (
                  <span className="font-bold text-sm">{ticket.id}</span>
                )}
                {ticket.isComeback && <span className="text-base">🚨</span>}
                {/* Comeback Resolution Badge for completed comeback tickets */}
                {ticket.isComeback && ticket.status === 'Completed' && (ticket as any).comeback_resolution && (
                  <Badge
                    variant={
                      (ticket as any).comeback_resolution === 'tech_fault'
                        ? 'destructive'
                        : (ticket as any).comeback_resolution === 'parts_failure'
                        ? 'secondary'
                        : 'outline'
                    }
                    className="text-xs"
                  >
                    {(ticket as any).comeback_resolution === 'tech_fault' && 'Tech Fault'}
                    {(ticket as any).comeback_resolution === 'parts_failure' && 'Parts Failure'}
                    {(ticket as any).comeback_resolution === 'unrelated' && 'Unrelated'}
                  </Badge>
                )}
              </div>
              {/* Kebab menu for locked tickets - positioned inline with customer name */}
              {!ticket.vehiclePresent && !hideKebabMenu && (
                <div className="flex items-center gap-1">
                  <TicketKebabMenu
                    ticketId={ticket.id}
                    isInParkingLot={isInParkingLot}
                    technicians={technicians}
                    assignedTechId={ticket.assignedTechId}
                    jobCount={ticketJobs.length}
                    isSplitTicket={!!(ticket as any).split_group_id}
                    status={ticket.status}
                    vehiclePresent={ticket.vehiclePresent}
                    ticketType={ticket.type}
                    scheduledStartTime={(ticket as any).scheduled_start_time}
                    dueOut={(ticket as any).due_out}
                    isComeback={ticket.isComeback}
                    predictedMinutes={ticket.predictedMinutes}
                    timerStatus={localTimerState.timer_status}
                    needsDiagnosticOutcome={(ticket as any).needs_diagnostic_outcome}
                    confirmationStatus={(ticket as any).confirmation_status}
                    teamId={(ticket as any).team_id}
                    onAddJob={() => setIsAddJobDialogOpen(true)}
                    onDelete={onDelete}
                    onSplit={() => setIsManualSplitModalOpen(true)}
                    onMerge={() => setShowMergeConfirm(true)}
                      onReassign={onReassign}
                  />
                  {isDraggable && (
                    <div 
                      {...attributes} 
                      {...listeners}
                      className="cursor-grab active:cursor-grabbing p-1 rounded hover:bg-muted/50 transition-colors"
                    >
                      <GripVertical className="h-4 w-4 text-muted-foreground" />
                    </div>
                  )}
                </div>
              )}
            </div>
            {ticket.vehicle ? (
              <div data-testid="ticket-vehicle-info">
                <p className="text-sm font-medium">
                  {ticket.vehicle.year} {ticket.vehicle.make} {ticket.vehicle.model}
                  {ticket.vehicle.engineSize ? ` • ${formatEngineSizeDisplay(ticket.vehicle.engineSize)}` : ''}
                </p>
                {/* OCT28 RECOVERY #1: plate + issuing state and the VIN, decrypted server-side, for canViewVehicleIdentity
                    viewers; the VIN opens this ticket's vehicle record. An encrypted value is never printed. */}
                {(() => {
                  const plate = cardPlateLabel(vehicleIdentity?.license_plate ?? ticket.vehicle.licensePlate,
                    vehicleIdentity?.plate_jurisdiction ?? (ticket.vehicle as any).plateJurisdiction);
                  const vin = cardVinLabel(vehicleIdentity?.vin);
                  if (!plate && !vin) return null;
                  return (
                    <p className="text-xs text-muted-foreground flex flex-wrap items-center gap-x-2" data-testid="ticket-vehicle-identity">
                      {plate && <span data-testid="ticket-vehicle-plate">{plate}</span>}
                      {vin && (
                        <button
                          type="button"
                          className="font-mono text-primary underline-offset-2 hover:underline"
                          data-testid="ticket-vehicle-vin-link"
                          title="Open this vehicle's record"
                          onPointerDown={(e) => e.stopPropagation()}
                          onClick={(e) => { e.stopPropagation(); setDetailsTarget({ tab: 'vehicle' }); setIsDetailsModalOpen(true); }}
                        >
                          VIN {vin}
                        </button>
                      )}
                    </p>
                  );
                })()}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No vehicle info</p>
            )}
          </div>
        </div>

        {/* Collapsible content - hide when collapsed in parking lot */}
        {!isCollapsed && (
          <>

        {/* Jobs */}
        <div className="mb-3" data-testid="ticket-jobs-list">
          <p className="text-xs font-medium text-muted-foreground mb-1">Jobs:</p>
          {/* OCT20 WS-ROLL: a continuation day carries no job lines — show the repair's (head-owned) jobs, read-only. */}
          {ticketJobs.length === 0 && (ticket as any).rollover_of_ticket_id && <ContinuationRepairJobs ticketId={ticket.id} />}
          {ticketJobs.map((job, idx) => {
            const isCompletedDiag = (job as any).separateTime || (job as any).diagnosticCompletedAt || (job as any).jobStatus === 'completed' || (job as any).jobStatus === 'skipped';
            return (
            <div key={idx} className="flex items-center justify-between text-sm group">
              <div className="flex items-center gap-2 flex-1">
                {isCompletedDiag && <CheckCircle2 className="h-3.5 w-3.5 text-green-500 shrink-0" />}
                <span 
                  className={cn(
                    "transition-colors",
                    isCompletedDiag && "text-muted-foreground line-through",
                    permissions.canEditTickets && !isCompletedDiag && "cursor-pointer hover:text-primary hover:underline"
                  )}
                  onClick={() => {
                    if (!permissions.canEditTickets) return;
                    // oct20e LABOR P3: a completed diagnostic is re-timed only by an audited correction (FD-2026-10-24-B)
                    if ((job as any).diagnosticCompletedAt) { setCorrectingJob({ id: job.id, name: job.name, hours: job.standardMinutes / 60 }); return; }
                    setEditingJob({
                      id: job.id,
                      name: job.name,
                      hours: job.standardMinutes / 60,
                      notes: '',
                    });
                  }}
                >
                  • {job.name}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">{job.standardMinutes} min</span>
                {/* Show allocated actual time for completed tickets with multiple jobs */}
                {ticket.status === 'Completed' && (ticket as any).actually_started && recordedMinutes !== null && ticketJobs.length > 1 && (job as any).actual_minutes !== undefined && (job as any).actual_minutes !== null && (
                  <span className="text-xs text-green-600 dark:text-green-400 font-medium">
                    ({Math.round((job as any).actual_minutes)} min)
                  </span>
                )}
                {/* oct20e LABOR P4: one-click Trash only for a never-started line; otherwise the plain reason (CORE-AG :74) */}
                {permissions.canEditTickets && (
                  <JobLineTrashControl state={jobTrash.stateFor(job.id)} onTrash={() => handleDeleteJob(job.id)} />
                )}
              </div>
            </div>
            );
          })}
        </div>

        {/* OptiDVI Section */}
        <TicketDviSection 
          ticketId={ticket.id} 
          shopId={(ticket as any).shop_id} 
          vehicle={ticket.vehicle}
          onJobsAdded={() => setJobsRefreshKey(prev => prev + 1)}
        />

        {/* Time & Timer */}
        <div className="space-y-1.5">
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Clock className="h-3 w-3" />
            <span>{totalMinutes} min</span>
          </div>
  {(() => {
    const customerName = (ticket as any).customer_name || ticket.customerName;
    const hasTimerOverride = isMarketingMode && getTimerOverride(customerName);
    const shouldShowTimer = ((ticket.status === 'Active' || ticket.status === 'Paused') && localTimerState.actually_started) || hasTimerOverride;
    
    if (!shouldShowTimer) return null;
    
    return (
      <div className={cn(
        "flex items-center gap-1 text-xs font-mono font-semibold",
        localTimerState.timer_status === 'running' || hasTimerOverride ? "text-green-600 dark:text-green-400" : "text-yellow-600 dark:text-yellow-500"
      )}>
        {localTimerState.timer_status === 'running' || hasTimerOverride ? <Play className="h-3 w-3" /> : <Pause className="h-3 w-3" />}
        <span>{formatElapsedTime(elapsedSeconds)}</span>
      </div>
    );
  })()}
        </div>

        {/* Start Time & Validation */}
        <div className="mt-2 pt-2 border-t space-y-1" data-testid="ticket-time-section">
          <div className="flex items-center justify-between text-xs" data-testid="ticket-start-time">
            <span className="text-muted-foreground">Start:</span>
            <div className="flex items-center gap-1.5">
              <span className="font-medium">
                {(() => {
                  // Check for Film Mode time display override
                  const customerName = (ticket as any).customer_name || ticket.customerName;
                  const timeOverride = isMarketingMode ? getTimeDisplayOverride(customerName) : null;
                  
                  if (timeOverride?.startTime) {
                    return timeOverride.startTime;
                  }
                  
                  try {
                    const raw = (ticket as any).start_time as string | undefined;
                    if (raw) {
                      const utcDate = new Date(raw);
                      return formatTimeInShopTZ(utcDate);
                    }
                    return ticket.startTime;
                  } catch {
                    return ticket.startTime;
                  }
                })()}
              </span>
              {/* Bump indicator - only show if ticket has been bumped */}
              {/* oct20e (Bucket 2 :440 vs :319): once started, the General AutoBump count lives in pre_start_bump_minutes */}
              {(ticket as any).original_start_time && ((ticket as any).actually_started ? (ticket as any).pre_start_bump_minutes : (ticket as any).accumulated_bump_minutes) > 0 && (
                <HoverCard>
                  <HoverCardTrigger asChild>
                    <TrendingUp className="h-3.5 w-3.5 text-amber-500 cursor-pointer hover:text-amber-600 transition-colors" />
                  </HoverCardTrigger>
                  <HoverCardContent className="w-64 p-3">
                    <div className="space-y-2">
                      <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                        Schedule Changes
                      </div>
                      <div className="space-y-1.5 text-sm">
                        <div className="flex justify-between items-center">
                          <span className="text-muted-foreground">Original Start:</span>
                          <span className="font-medium">
                            {formatTimeInShopTZ(new Date((ticket as any).original_start_time))}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-muted-foreground">Current Start:</span>
                          <span className="font-medium">
                            {(() => {
                              try {
                                const raw = (ticket as any).start_time as string | undefined;
                                if (raw) return formatTimeInShopTZ(new Date(raw));
                                return ticket.startTime;
                              } catch {
                                return ticket.startTime;
                              }
                            })()}
                          </span>
                        </div>
                        <div className="flex justify-between items-center pt-1 border-t">
                          <span className="text-amber-600 font-semibold">Bumped:</span>
                          <span className="text-amber-600 font-semibold">
                            +{((ticket as any).actually_started ? (ticket as any).pre_start_bump_minutes : (ticket as any).accumulated_bump_minutes)} minutes
                          </span>
                        </div>
                      </div>
                    </div>
                  </HoverCardContent>
                </HoverCard>
              )}
              {startTimeValidation.length > 0 && (
                <AlertTriangle className="h-3 w-3 text-warning" />
              )}
            </div>
          </div>
          {startTimeValidation.length > 0 && (
            <div className="text-xs text-warning">
              {startTimeValidation.map((warning, idx) => (
                <div key={idx}>⚠ {warning}</div>
              ))}
            </div>
          )}
          
          {technician && (
            <>
              <div className="flex items-center justify-between text-xs" data-testid="ticket-optitime">
                <span className="text-muted-foreground">Tech OptiTime:</span>
                <span className="font-medium text-primary">{calculateOptiTime} min</span>
              </div>
              {/* Slice 5D-11 Batch 1 — advisor-aware Bay Occupancy block.
                  Flag-gated. Display-only — does NOT feed scheduling or
                  technician efficiency. Tech OptiTime row above is kept
                  unchanged so the separation between tech work and
                  advisor-attributable bay hold is visually obvious. */}
              {advisorBayOccupancyOnDayboardEnabled && bayOccupancyEstimate?.shouldRender && (
                <div
                  className="rounded border border-dashed border-muted-foreground/30 bg-muted/20 -mx-2 px-2 py-1 text-xs space-y-0.5"
                  data-testid="ticket-bay-occupancy-block"
                >
                  {bayOccupancyEstimate.advisorBayHold.reason === 'value'
                    && bayOccupancyEstimate.advisorBayHoldMinutes !== null && (
                      <div
                        className="flex items-center justify-between"
                        data-testid="ticket-advisor-bay-hold"
                      >
                        <span className="text-muted-foreground">Advisor Bay Hold:</span>
                        <span className="font-medium">
                          {/* Round to whole minutes via the shared helper —
                              the underlying value can be a long decimal
                              (e.g. 2.97261…) coming straight from the
                              raw average; users want "+3 min", not the
                              raw float. */}
                          {formatAdvisorBayHoldMinutesForDisplay(
                            bayOccupancyEstimate.advisorBayHoldMinutes,
                          )}{' '}
                          <span className="text-muted-foreground">
                            {bayOccupancyEstimate.advisorBayHold.source === 'observed'
                              ? 'observed'
                              : 'predicted'}
                          </span>
                        </span>
                      </div>
                    )}
                  {bayOccupancyEstimate.advisorBayHold.reason === 'parking_lot' && (
                    <div
                      className="text-muted-foreground italic"
                      data-testid="ticket-advisor-bay-hold-parking-lot"
                    >
                      Vehicle in parking lot — bay hold N/A
                    </div>
                  )}
                  {bayOccupancyEstimate.advisorBayHold.reason === 'unknown_bay_presence' && (
                    <div
                      className="text-muted-foreground italic"
                      data-testid="ticket-advisor-bay-hold-unknown"
                    >
                      Bay hold may apply
                    </div>
                  )}
                  {bayOccupancyEstimate.advisorBayHold.reason === 'insufficient_history' && (
                    <div
                      className="text-muted-foreground italic"
                      data-testid="ticket-advisor-bay-hold-insufficient"
                    >
                      Advisor Bay Hold: not enough history yet
                    </div>
                  )}
                  {/*
                    `Bay Occupancy` and `Bay Finish` rows removed
                    (display polish). The Start / Tech OptiTime / OptiFinish
                    rows above and below already express the timing the
                    user needs; these were duplicative. The underlying
                    `bayOccupancyEstimate` is still computed (callers may
                    use it elsewhere) — only its on-card lines are gone.
                  */}
                </div>
              )}
              {/* Phase B — Run Long sourced from canonical derivedTime record.
                  Same math as before; the value is now also fed into OptiFinish
                  so the display row can never disagree with the finish time. */}
              {(derivedTime.runLongMinutes > 0 || (ticket as any).run_long_minutes > 0) && (
                <div className="flex items-center justify-between text-xs bg-red-100 dark:bg-red-950/50 -mx-2 px-2 py-1 border-l-2 border-red-500" data-testid="ticket-run-long">
                  <span className="text-red-900 dark:text-red-200 font-medium flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {derivedTime.displayLabels.runLongChipLabel}:
                  </span>
                  <span className="font-semibold text-red-900 dark:text-red-200">
                    +{derivedTime.runLongMinutes} min
                  </span>
                </div>
              )}
              {/* Phase B — pause-time chip is now channel-aware. The label
                  "AutoBump" (Bucket 2 §2.8.3 General AutoBump) is reserved for
                  Start-Time advancement; bay-blocking pause time shows as
                  "Pause AutoBump" (Bucket 2 §2.6.2) and non-bay hold time
                  shows as "Hold time" (Bucket 4.5 §4.5.8) — only the former
                  inflates OptiFinish, but both are surfaced so the user can
                  see the pause activity. */}
              {derivedTime.holdChannel === 'bay_blocking' && derivedTime.bayBlockingPauseMinutes > 0 && (
                <div
                  className="flex items-center justify-between text-xs bg-orange-100 dark:bg-orange-950/50 -mx-2 px-2 py-1 border-l-2 border-orange-500"
                  data-testid="ticket-pause-autobump"
                >
                  <span className="text-orange-900 dark:text-orange-200 font-medium flex items-center gap-1">
                    <TrendingUp className="h-3 w-3" />
                    Pause AutoBump:
                  </span>
                  <span className="font-semibold text-orange-900 dark:text-orange-200">
                    +{derivedTime.bayBlockingPauseMinutes} min
                  </span>
                </div>
              )}
              {derivedTime.holdChannel === 'non_bay' && derivedTime.nonBayHoldMinutes > 0 && (
                <div
                  className="flex items-center justify-between text-xs bg-blue-100 dark:bg-blue-950/50 -mx-2 px-2 py-1 border-l-2 border-blue-500"
                  data-testid="ticket-non-bay-hold"
                >
                  <span className="text-blue-900 dark:text-blue-200 font-medium flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    Hold time:
                  </span>
                  <span className="font-semibold text-blue-900 dark:text-blue-200">
                    +{derivedTime.nonBayHoldMinutes} min
                  </span>
                </div>
              )}
              {derivedTime.breakMinutes > 0 && (
                <div className="flex items-center justify-between text-xs bg-amber-100 dark:bg-amber-950/50 -mx-2 px-2 py-1 border-l-2 border-amber-500" data-testid="ticket-break-minutes">
                  <span className="text-amber-900 dark:text-amber-200 font-medium">Break:</span>
                  <span className="font-semibold text-amber-900 dark:text-amber-200">{derivedTime.breakMinutes} min</span>
                </div>
              )}
              {/* CORE-AC: the finish time and what it rests on are ONE unit.
                  The first version of this disclosure rendered under Tech
                  OptiTime, several conditional rows away from the number it
                  described; OptiFinishRow exists so they cannot drift apart
                  again. Fed from the SAME technician object the finish time
                  was derived from. */}
              <OptiFinishRow
                displayValue={(() => {
                  // Film Mode time display override
                  const customerName = (ticket as any).customer_name || ticket.customerName;
                  const timeOverride = isMarketingMode ? getTimeDisplayOverride(customerName) : null;
                  return timeOverride?.optiFinish ?? calculateOptiFinish;
                })()}
                requiresRolloverGate={derivedTime.requiresRolloverGate}
                technician={{
                  efficiencyPercentage: technician.efficiencyPercentage,
                  efficiencySource: technician.efficiencySource,
                  efficiencySampleSize: technician.efficiencySampleSize,
                }}
              />
              {/* Phase D — surface the EOD rollover gate so the OptiFinish is
                  never silently rendered past the tech's shift end. This is
                  display-only; useEODMonitor above still owns the actual
                  rollover dispatch + modal (Bucket 3 §3.12.1). */}
              {/* EOD gate wording is ROLE-DEPENDENT, because the gate names an
                  ACT and not everyone who can see the card can perform it.
                  "Needs rollover decision" was previously rendered to every
                  viewer — including the assigned technician, who has no way to
                  resolve it and, for a never-started ticket, no rollover to
                  make. Bucket 5 §5.11 gives the technician risk visibility;
                  §5.6.8 keeps warnings out of the decision business.

                  Three cases, in the order they are decided:
                    · no placement authority → neutral timing fact
                    · virgin                 → a SCHEDULING decision
                    · started                → the existing rollover decision */}
              {eodGatePresentation === 'extends_past_shift' && (
                <div
                  className="flex items-center justify-between text-xs bg-yellow-50 dark:bg-yellow-950/40 -mx-2 px-2 py-1 border-l-2 border-yellow-500"
                  data-testid="ticket-eod-extends-past-shift"
                >
                  <span className="text-yellow-900 dark:text-yellow-200 font-medium flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    Extends past shift
                  </span>
                </div>
              )}
              {eodGatePresentation === 'scheduling_decision' && (
                <div
                  className="flex items-center justify-between text-xs bg-orange-50 dark:bg-orange-950/40 -mx-2 px-2 py-1 border-l-2 border-orange-500"
                  data-testid="ticket-eod-scheduling-decision"
                >
                  <span className="text-orange-900 dark:text-orange-200 font-medium flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    Cannot finish today — scheduling decision
                  </span>
                </div>
              )}
              {eodGatePresentation === 'rollover_decision' && (
                <div
                  className="flex items-center justify-between text-xs bg-orange-50 dark:bg-orange-950/40 -mx-2 px-2 py-1 border-l-2 border-orange-500"
                  data-testid="ticket-eod-rollover-gate"
                >
                  <span className="text-orange-900 dark:text-orange-200 font-medium flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    Needs rollover decision
                  </span>
                </div>
              )}
              {!derivedTime.requiresRolloverGate && derivedTime.isAtEodRisk && (
                <div
                  className="flex items-center justify-between text-xs bg-yellow-50 dark:bg-yellow-950/40 -mx-2 px-2 py-1 border-l-2 border-yellow-500"
                  data-testid="ticket-eod-risk"
                >
                  <span className="text-yellow-900 dark:text-yellow-200 font-medium flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    Approaching shift end
                  </span>
                </div>
              )}
            </>
          )}
          
          {/* Rollover indicator (simplified — no Part X / Previous / Next) */}
          {(ticket as any).rollover_of_ticket_id && (
            <div className="mt-2 text-xs text-orange-600 dark:text-orange-400">
              {/* Core-100 R2 CORE-BE: a carried ticket says why it is here. */}
              <CarryReasonLine
                ticketId={ticket.id}
                reason={(ticket as any).carry_reason}
                basis={(ticket as any).carry_reason_basis}
                canState={permissions.canEditTickets}
              />
            </div>
          )}
          
          {/* Recorded Time for completed tickets with timer started */}
          {ticket.status === 'Completed' && (ticket as any).actually_started && recordedMinutes !== null && (
            <div className="flex items-center justify-between text-xs bg-green-500/10 -mx-2 px-2 py-1">
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">Recorded Time:</span>
                <span className="font-medium text-green-600 dark:text-green-400" data-testid="recorded-time">{recordedMinutes} min</span>
              </div>
              {(permissions.isManager || permissions.isOwner) && ticketJobs && ticketJobs.length > 1 && (
                <EditTimersModal
                  ticketId={ticket.id}
                  jobs={ticketJobs}
                  onUpdate={() => setJobsRefreshKey(prev => prev + 1)}
                />
              )}
            </div>
          )}
          
          {ticket.dueOut && (
            <div className="text-xs">
              <span className="text-muted-foreground">Due: </span>
              <span className="font-medium">{formatShopTime((ticket as any).due_out || ticket.dueOut, shopTimezone, 'short')}</span>
            </div>
          )}
        </div>

        {/* Parking Lot Metadata - Show who moved it and when */}
        {isInParkingLot && (ticket as any).last_edited_at && (
          <div className="mt-2 p-2 bg-amber-50 dark:bg-amber-900/20 rounded text-xs border-l-2 border-amber-500">
            <div className="font-medium text-amber-700 dark:text-amber-400">Moved to parking lot:</div>
            <div className="mt-1 text-foreground">
              by <span className="font-medium">{movedByUserName || 'User'}</span> on{' '}
              {new Date((ticket as any).last_edited_at).toLocaleString('en-US', {
                month: '2-digit',
                day: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
              })}
            </div>
            {/* Show parking lot reason if available */}
            {(ticket as any).parking_lot_reason && (
              <div className="mt-1 text-muted-foreground">
                Reason: {(ticket as any).parking_lot_reason}
              </div>
            )}
            {/* Show total timer duration if available for completed tickets */}
            {ticket.status === 'Completed' && (ticket as any).actually_started && recordedMinutes !== null && (
              <div className="mt-2 pt-2 border-t border-amber-500/20">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Timer Duration:</span>
                  <span className="font-medium text-green-600 dark:text-green-400">{recordedMinutes} min</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Ticket Timer Controls - NEW PHASE 1 */}
        {ticket.status !== 'Completed' && !shouldHideTimerControls && timerSurfaceIsStartable && (
          <div className="mt-3 pt-3 border-t">
            <div className="flex items-center justify-between mb-2">
              <TicketTimerDisplay
                ticketId={ticket.id}
                timerStatus={localTimerState.timer_status || 'stopped'}
                liveElapsedMinutes={derivedTime.liveElapsedMinutes}
              />
              <EditTimersModal 
                ticketId={ticket.id}
                jobs={ticketJobs.map(j => ({
                  id: j.id,
                  name: j.name,
                  standardMinutes: j.standardMinutes,
                  actual_minutes: (j as any).actual_minutes
                }))}
                onUpdate={() => setJobsRefreshKey(prev => prev + 1)}
              />
            </div>
            <TicketTimerControls
              ticketId={ticket.id}
              techId={ticket.assignedTechId ?? null}
              requiresPullIn={timerNeedsPullIn}
              shopId={(ticket as any).shop_id}
              timerStatus={localTimerState.timer_status || 'stopped'}
              onTimerUpdate={refreshTimerState}
              customerName={(ticket as any).customer_name || ticket.customerName}
              hasFilmModeOverride={isMarketingMode && !!getTimerOverride((ticket as any).customer_name || ticket.customerName)}
              canDecideEndOfDay={permissions.canAssignTickets}
              canOverridePreauth={permissions.canOverridePreauth}
            />
          </div>
        )}
        {/* A final Stop completes the ticket while its time review may still
            be open. The completed card offers exactly that review and nothing
            else (renders nothing when this viewer has none). */}
        {ticket.status === 'Completed' && !shouldHideTimerControls && (
          <TicketTimerControls
            ticketId={ticket.id}
            techId={ticket.assignedTechId ?? null}
            shopId={(ticket as any).shop_id}
            timerStatus={localTimerState.timer_status || 'stopped'}
            onTimerUpdate={refreshTimerState}
            reviewOnly
          />
        )}
          </>
        )}
      </div>

      {/* Dialogs */}
      {/* REMOVED: isPauseDialogOpen Dialog and isStopWarningOpen AlertDialog — dead code.
          Live timer UI is TicketTimerControls. See Round 37, FIX 2. */}


      {/* Due Out Mitigation Modal */}
      {dueOutViolation && (
        <DueOutMitigationModal
          isOpen={isDueOutModalOpen}
          onClose={() => setIsDueOutModalOpen(false)}
          ticket={ticket}
          currentTechName={technician?.name || 'Unknown'}
          dueOutTime={dueOutViolation.dueOutTime}
          predictedFinish={dueOutViolation.predictedFinish}
          technicians={technicians}
          onActionComplete={() => {
            // Refresh ticket data after action — routes through the
            // centralized cross-device invalidation helper.
            void notifyDayboardInvalidated({
              reason: 'ticket-edit',
              shopId: (ticket as any).shop_id || activeShop?.id || '',
              ticketId: ticket.id,
            });
          }}
        />
      )}

      {/* FINISH WORK — the deliberate human act CORE-AL requires.
          DELIBERATELY OUTSIDE TicketTimerControls. It used to render inside a
          block gated on `!shouldHideTimerControls && ticket.assignedTechId`,
          which meant completion inherited TIMER permission and required an
          assigned technician. A SERVICE_WRITER and a DISPATCHER — both of whom
          the server lets complete — could not see the control at all, and a
          ready but unassigned ticket could never be finished from this card.
          All-jobs-done only makes this VISIBLE; it never fires it, and the
          server re-checks every obligation under lock regardless of what this
          component believes. */}
      {readyToComplete && (permissions.canCompleteTickets || detailSpecialistMayFinish) && (
        <div className="px-3 pb-3">
          <Button
            size="sm"
            variant="default"
            disabled={isUpdating}
            onClick={() => setCompletionLocationOpen(true)}
            data-testid="finish-work-button"
          >
            {isUpdating ? 'Finishing…' : 'Finish Work'}
          </Button>
        </div>
      )}

      {/* COMPLETION LOCATION — collected BEFORE the terminal call, never after.
          `tickets_lifecycle_terminal_state` is an IMMEDIATE check that forbids
          a completed ticket holding a bay, so "complete now, park later" is
          rejected at the first statement; the answer has to exist first.
          `requireExplicitSelection` turns off the ordinary-move default of
          General Lot so a silence cannot become the record. */}
      <ParkingSpotPickerDialog
        isOpen={completionLocationOpen}
        onClose={() => {
          // Cancel asserts nothing and completes nothing.
          setCompletionLocationOpen(false);
          setPendingCompletionLocation(null);
        }}
        ticketId={ticket.id}
        shopId={(ticket as any).shop_id || activeShop?.id || ''}
        selectionOnly
        requireExplicitSelection
        onSelect={(spotId) => { void submitCompletionWithLocation(spotId); }}
      />

      {/* Comeback Resolution Modal */}
      <ComebackResolutionModal
        isOpen={showComebackModal}
        onResolve={handleComebackResolution}
        ticketId={ticket.id}
      />

      {/* Vehicle Check-In Dialog (opened by double-click on the locked banner) */}
      <CheckInVehicleDialog
        isOpen={isCheckInDialogOpen}
        onClose={() => setIsCheckInDialogOpen(false)}
        ticketId={ticket.id}
        shopId={(ticket as any).shop_id || activeShop?.id || ''}
        customerName={ticket.customerName}
        customerPhone={ticket.customerPhone}
        vehicleSummary={
          ticket.vehicle
            ? `${ticket.vehicle.year ?? ''} ${ticket.vehicle.make ?? ''} ${ticket.vehicle.model ?? ''}`.trim() || null
            : null
        }
        vehiclePlate={ticket.vehicle?.licensePlate ?? null}
        vehicleVin={ticket.vehicle?.vin ?? null}
        jobs={ticketJobs.map(j => ({
          id: j.id,
          name: j.name ?? null,
          jobLabel: (j as any).jobLabel ?? null,
          minutes: typeof j.standardMinutes === 'number' ? j.standardMinutes : null,
        }))}
        ticketType={ticket.type}
        // the raw timestamp — `ticket.startTime` is the Dayboard's display string ("10:56 AM")
        scheduledStartTime={(ticket as any).start_time ?? null}
        shopTimezone={shopTimezone}
        // Feed the tech the modal will preserve into scheduled_tech_id.
        // Fall back to the existing scheduled_tech_id when the ticket has
        // already been parked once (assigned_tech_id is null but the plan
        // survives via the new column from migration 20260601160000).
        assignedTechId={
          ticket.assignedTechId ?? (ticket as any).scheduled_tech_id ?? null
        }
        assignedTechName={technician?.name ?? null}
        onComplete={() => {
          // Optimistic local flip — the realtime subscription will reconcile.
          setLocalVehiclePresent(true);
        }}
      />

      {/* Manual Split Modal */}
      <AlertDialog open={showMergeConfirm} onOpenChange={setShowMergeConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Merge these split parts back together?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>
                  The parts of this split will become one live ticket again. All of
                  their job lines move across as they are — nothing is re-created,
                  and no recorded time is changed.
                </p>
                <p>
                  This only works while every part is still incomplete and nobody is
                  working on it. If a part has been completed or picked up, or has
                  time recorded against it, the merge will be refused and explained
                  rather than forced.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isMerging}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); void handleConfirmMerge(); }}
              disabled={isMerging}
              data-testid="confirm-merge-split-parts"
            >
              {isMerging ? 'Merging…' : 'Merge parts'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ManualSplitModal
        isOpen={isManualSplitModalOpen}
        onClose={() => setIsManualSplitModalOpen(false)}
        ticketId={ticket.id}
        ticketJobs={ticketJobs.map(j => ({
          id: j.id,
          name: j.name,
          category: j.category || '',
          standardMinutes: j.standardMinutes || 0,
        }))}
        currentTechId={ticket.assignedTechId || null}
        currentTechName={technician?.name || 'Unassigned'}
        technicians={technicians}
        shopId={(ticket as any).shop_id || ''}
        timerStatus={localTimerState.timer_status}
        vehicleInfo={{
          year: ticket.vehicle?.year,
          make: ticket.vehicle?.make,
          model: ticket.vehicle?.model,
        }}
      />

      {/* Edit Single Job Dialog */}
      <EditSingleJobDialog
        isOpen={!!editingJob}
        onClose={() => setEditingJob(null)}
        jobName={editingJob?.name || ''}
        currentHours={editingJob?.hours || 0}
        onConfirm={handleEditJob}
      />
      {/* oct20e LABOR P3: audited correction of a completed diagnostic's time */}
      <CompletedDiagnosticCorrectionDialog
        ticketId={ticket.id}
        job={correctingJob}
        canCorrect={permissions.isManager || permissions.isOwner}
        onClose={() => setCorrectingJob(null)}
        onCorrected={() => { setJobsRefreshKey(prev => prev + 1); fetchTicketJobsWithCompletionFallback(); onTicketUpdated?.(); }}
      />
      {/* Ticket Details Popup - Shows all ticket info */}
      <TicketDetailsPopup
        isOpen={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
        ticket={ticket}
        technician={technician}
        ticketJobs={ticketJobs}
        elapsedSeconds={elapsedSeconds}
        calculateOptiTime={calculateOptiTime}
        calculateOptiFinish={calculateOptiFinish}
        totalMinutes={totalMinutes}
        bayName={bayName}
        initialTab={detailsTarget?.tab}
        focusSection={detailsTarget?.section}
      />
    </Card>
  );
};
